import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import AdPreviewModal from "./AdPreviewModal";
import {
  ArrowLeft,
  Eye,
  MousePointer,
  TrendingUp,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  UserPlus,
  Sparkles,
  Loader2,
  Calendar,
  Facebook,
  Instagram,
  Users,
  Pencil,
  Check,
  X,
  Image,
  Link,
  ExternalLink,
  Copy,
  Clock,
  Plus,
  Trash2,
  ChevronDown,
  MapPin,
  Smartphone,
  Monitor,
  Tv,
  Globe,
  Target,
  Search,
  ShoppingBag,
  Info,
  AlertTriangle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom Leaflet marker icons
const cityIcon = L.divIcon({
  className: '',
  html: `<div style="background:#3b82f6;width:14px;height:14px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10],
});
const geofenceIcon = L.divIcon({
  className: '',
  html: `<div style="background:#f59e0b;width:12px;height:12px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -8],
});

// Extracted components
import { 
  CustomerJourney,
  MarketingChannelsSection,
  BrandConsistencySection,
  SectionReportHeader,
  PPCSearchGuideModal,
  MarketingChannelGuideModal,
  AudienceTargeting,
  AppFrontSection,
  GoogleAdsSection,
  ContentEffectCard,
  CollapsibleCard,
  ROIStoryHero,
  LandingPageAnalysis, // eslint-disable-line no-unused-vars -- kept for potential re-enable
  ConversionChecklist,
  ReportAtAGlance,
  Chapter,
  ChapterDropdown,
  StickyChapterNav, // eslint-disable-line no-unused-vars -- kept for potential re-enable
  BrandManifesto,
  BrandValueFlywheel
} from "./client-detail";
import { TikTokSection } from "./client-detail/TikTokSection";
import PmaxStoryCard from "./client-detail/PmaxStoryCard";
import MetaStoryLayout from "./client-detail/meta/MetaStoryLayout";
import MobileStoryLayout from "./client-detail/story/MobileStoryLayout";
import SocialMirrorStoryLayout from "./client-detail/story/SocialMirrorStoryLayout";
import { pmaxInPeriod, pmaxTotals } from "./client-detail/pmax/pmaxUtils";
import { usePreviousPeriod } from "./client-detail/compare/usePreviousPeriod";
import { useAgencyNote } from "./client-detail/useAgencyNote";
import { friendlyRange } from "./client-detail/compare/headlineTotals";
import { ReportPeriodEditor } from "./client-detail/ReportPeriodEditor";
import { buildChannelStories } from "./client-detail/channels/channelStories";
import { ZipTownList } from "./client-detail/ZipTownList";
import { SocialMirrorDaily } from "./client-detail/story/SocialMirrorDaily";
import { CampaignSummaryDaily } from "./client-detail/channels/CampaignSummaryDaily";
import { CtvMonths, CtvTiles } from "./client-detail/ctv/CtvParts";
import { CtvPublisherName } from "./client-detail/ctv/CtvPublisherName";
import { Package } from "lucide-react";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const MetricCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100" data-testid={`detail-metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-xl font-bold text-slate-900 font-['Outfit']">
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DEFAULT_CENTER = [38.0, -78.5];
// Guard against stray lookups landing outside the US (incl. AK/HI)
const inUS = (p) => p.lat >= 18 && p.lat <= 72 && p.lng >= -180 && p.lng <= -64;

// Center of a zip feature: stored center, else middle of its outline
const zipCenter = (f) => {
  const c = f.properties?.center;
  if (c?.lat != null) return [c.lat, c.lng];
  const g = f.geometry;
  const ring = g?.type === 'Polygon' ? g.coordinates[0] : g?.type === 'MultiPolygon' ? g.coordinates[0][0] : null;
  if (!ring?.length) return null;
  const lats = ring.map((p) => p[1]);
  const lngs = ring.map((p) => p[0]);
  return [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
};

const GeoMap = ({ location, geofencingLocations = [], targetZipcodes = [], API, onTypes }) => {
  const mapRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [zipBoundaries, setZipBoundaries] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  // Fetch zip code boundaries
  useEffect(() => {
    if (!targetZipcodes || targetZipcodes.length === 0) { setZipBoundaries(null); return; }
    let cancelled = false;
    const fetchBoundaries = async () => {
      try {
        const res = await fetch(`${API}/zipcode-boundaries?zipcodes=${encodeURIComponent(targetZipcodes.join(','))}`);
        const data = await res.json();
        if (!cancelled) setZipBoundaries(data);
      } catch(e) { console.error("Failed to fetch zip boundaries:", e); }
    };
    fetchBoundaries();
    return () => { cancelled = true; };
  }, [targetZipcodes, API]);

  useEffect(() => {
    if (!location && geofencingLocations.length === 0 && (!targetZipcodes || targetZipcodes.length === 0)) { setPoints([]); return; }
    let cancelled = false;

    const geocodeOne = async (q) => {
      const res = await fetch(`${API}/geocode?q=${encodeURIComponent(q)}`);
      return res.json();
    };

    const geocodeAll = async () => {
      const allPts = [];

      // 1. Geocode geo_targeting locations (city-level, blue markers)
      if (location) {
        // First, extract radius targets like "3 mile radius around 100 Main St, City, ST"
        const radiusPattern = /(\d+(?:\.\d+)?)\s*mile\s*radius\s*around\s+(.+?)(?=\d+(?:\.\d+)?\s*mile\s*radius|$)/gi;
        let radiusMatch;
        const radiusTargets = [];
        while ((radiusMatch = radiusPattern.exec(location)) !== null) {
          const miles = parseFloat(radiusMatch[1]);
          const address = radiusMatch[2].replace(/[;,\s]+$/, '').trim();
          if (address) radiusTargets.push({ miles, address });
        }

        // Geocode radius targets
        const radiusResults = await Promise.all(radiusTargets.map((rt) => geocodeOne(rt.address).catch(() => [])));
        radiusResults.forEach((data, i) => {
          const rt = radiusTargets[i];
          if (data.length > 0) {
            allPts.push({
              lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon),
              label: `${rt.miles} mi radius: ${rt.address}`,
              type: 'radius', radiusMeters: rt.miles * 1609.34,
            });
          }
        });

        // Remove radius phrases; zips (and "20105 Aldie, VA" pairs) are drawn as zip areas, not pins
        const cleanedLocation = location.replace(radiusPattern, '')
          .replace(/\b\d{5}\s+[A-Za-z .'-]+?,?\s*[A-Z]{2}\b/g, ' ')
          .replace(/\b\d{5}\b/g, ' ')
          .replace(/^[\s,;]+|[\s,;]+$/g, '')
          .replace(/(\s*,\s*){2,}/g, ', ')
          .trim();

        // Split by semicolons or newlines first
        const parts = cleanedLocation.split(/[;]|\n/).map(s => s.trim()).filter(Boolean);
        let locs = parts;
        if (parts.length === 1 && cleanedLocation) {
          // Try "City, ST" patterns (with or without comma before state)
          const matches = cleanedLocation.match(/[A-Za-z .'-]+,?\s*[A-Z]{2}\b/g);
          if (matches && matches.length > 1) {
            locs = matches.map(s => s.trim());
          }
        }
        // Only geocode non-empty cleaned parts
        const cityLocs = locs.map((l) => l && l.trim()).filter((l) => l && l.length >= 2);
        const cityResults = await Promise.all(cityLocs.map((l) => geocodeOne(l).catch(() => [])));
        cityResults.forEach((data, i) => {
          if (data.length > 0) allPts.push({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), label: cityLocs[i], type: 'city' });
        });
      }

      // 2. Geocode geofencing locations (address-level, orange markers)
      const uniqueAddresses = [];
      const seen = new Set();
      for (const gf of geofencingLocations) {
        const addr = gf.city ? `${gf.address}, ${gf.city}, ${gf.state} ${gf.zip || ''}`.trim() : gf.address;
        const label = gf.name || addr;
        if (!seen.has(addr)) { seen.add(addr); uniqueAddresses.push({ addr, label, impressions: gf.impressions, clicks: gf.clicks }); }
      }
      uniqueAddresses.sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
      const topAddresses = uniqueAddresses.slice(0, 15);
      const gfResults = await Promise.all(topAddresses.map((gf) => geocodeOne(gf.addr).catch(() => [])));
      gfResults.forEach((data, i) => {
        const gf = topAddresses[i];
        if (data.length > 0) {
          allPts.push({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), label: gf.label, type: 'geofence', impressions: gf.impressions, clicks: gf.clicks });
        }
      });

      if (!cancelled) { setPoints(allPts); onTypes?.(new Set(allPts.filter(inUS).map((p) => p.type))); }
    };
    geocodeAll();
    return () => { cancelled = true; };
  }, [location, geofencingLocations, API]);

  useEffect(() => {
    if (mapRef.current && (points.length > 0 || zipBoundaries?.features?.length > 0)) {
      const map = mapRef.current;
      const allLatLngs = [];
      for (const p of points.filter(inUS)) {
        allLatLngs.push([p.lat, p.lng]);
        // Expand bounds for radius circles
        if (p.type === 'radius' && p.radiusMeters) {
          const degOffset = p.radiusMeters / 111320;
          allLatLngs.push([p.lat + degOffset, p.lng]);
          allLatLngs.push([p.lat - degOffset, p.lng]);
          allLatLngs.push([p.lat, p.lng + degOffset]);
          allLatLngs.push([p.lat, p.lng - degOffset]);
        }
      }
      if (zipBoundaries?.features) {
        for (const f of zipBoundaries.features) {
          const c = zipCenter(f);
          if (c) {
            const off = 5000 / 111320;
            allLatLngs.push([c[0] + off, c[1] + off], [c[0] - off, c[1] - off]);
          }
        }
      }
      if (allLatLngs.length > 0) {
        const bounds = L.latLngBounds(allLatLngs);
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
      } else {
        map.setView(DEFAULT_CENTER, 8);
      }
    }
  }, [points, mapReady, zipBoundaries]);

  if (points.length === 0 && !location && (!targetZipcodes || targetZipcodes.length === 0)) return null;

  const usPoints = points.filter(inUS);
  const center = usPoints.length > 0
    ? [usPoints.reduce((s, p) => s + p.lat, 0) / usPoints.length, usPoints.reduce((s, p) => s + p.lng, 0) / usPoints.length]
    : DEFAULT_CENTER;

  return (
    <div className="rounded-lg overflow-hidden border border-white/10 h-80" data-testid="geo-map">
      <MapContainer
        center={center}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        whenReady={() => setMapReady(true)}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {usPoints.map((pt, i) => (
          <Marker key={i} position={[pt.lat, pt.lng]} icon={pt.type === 'city' ? cityIcon : geofenceIcon}>
            <Popup>
              <div style={{ color: '#1e293b', fontSize: 12 }}>
                <strong>{pt.label}</strong>
                {pt.type === 'geofence' && pt.impressions && (
                  <div>{pt.impressions.toLocaleString()} imp &middot; {pt.clicks} clicks</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        {/* Radius circles */}
        {usPoints.filter(pt => pt.type === 'radius').map((pt, i) => (
          <Circle
            key={`radius-${i}`}
            center={[pt.lat, pt.lng]}
            radius={pt.radiusMeters}
            pathOptions={{ fillColor: '#3b82f6', fillOpacity: 0.10, color: '#3b82f6', weight: 2, opacity: 0.5, dashArray: '6 4' }}
          >
            <Popup>
              <div style={{ color: '#1e293b', fontSize: 12 }}>
                <strong>{pt.label}</strong>
              </div>
            </Popup>
          </Circle>
        ))}
        {/* Target zip areas: one matching circle per zip */}
        {zipBoundaries?.features?.map((feature, i) => {
          const c = zipCenter(feature);
          if (!c) return null;
          const p = feature.properties;
          return (
            <Circle
              key={`zip-circle-${p.zipcode}-${i}`}
              center={c}
              radius={5000}
              pathOptions={{ fillColor: '#8b5cf6', fillOpacity: 0.20, color: '#7c3aed', weight: 2, opacity: 0.7 }}
            >
              <Popup>
                <div style={{ color: '#1e293b', fontSize: 13 }} data-testid={`zip-popup-${p.zipcode}`}>
                  <strong>{p.town || p.zipcode}</strong>{p.town && <span style={{ color: '#64748b' }}> · {p.zipcode}</span>}
                </div>
              </Popup>
            </Circle>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default function ClientReport({ client, metrics, adMetrics, onBack, onRefresh, onViewChange, API, readOnly = false, hideDatePill = false, periodControl = null }) {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [filteredMetrics, setFilteredMetrics] = useState([]);
  const [filteredAdMetrics, setFilteredAdMetrics] = useState([]);
  const [insight, setInsight] = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [localMetrics, setLocalMetrics] = useState([]);
  const [localAdMetrics, setLocalAdMetrics] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  
  // Multi-location state
  const [activeLocation, setActiveLocation] = useState("all"); // "all" or specific location name
  const [locationSummary, setLocationSummary] = useState([]);
  
  const [stats, setStats] = useState({
    impressions: 0,
    clicks: 0,
    ctr: 0,
    spend: 0,
    likes: 0,
    comments: 0,
    shares: 0,
    new_followers: 0,
  });
  
  // Campaign details editing state
  const [editingField, setEditingField] = useState(null); // 'geo' or 'audience'
  const [editValue, setEditValue] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [localClient, setLocalClient] = useState(client);

  // Collapsible sections (default collapsed to keep the page scannable)
  const [omniChannelOpen, setOmniChannelOpen] = useState(false);
  
  // Top creatives state
  const [topCreatives, setTopCreatives] = useState([]);
  const [loadingCreatives, setLoadingCreatives] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null); // ad_name being edited
  const [descriptionValue, setDescriptionValue] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  
  // PPC Modal state
  const [showPPCModal, setShowPPCModal] = useState(false);
  
  // Channel Guide Modal state
  const [showChannelGuide, setShowChannelGuide] = useState(false);
  
  // Marketing channels state
  const [marketingChannels, setMarketingChannels] = useState([]);
  
  // Traffic acquisition state
  const [trafficData, setTrafficData] = useState({ channels: [], totals: {} });
  // Content Effect — Meta Business Suite content/page-level metrics
  // (organic + boosted blended). Read from the isolated
  // meta_content_overview collection via /api/content-pulse/:id.
  const [contentEffectRecords, setContentEffectRecords] = useState([]);
  // Live Chat records — isolated live_chat_reports collection. Fetched
  // separately from all ad pipelines. Sorted newest first by the API.
  const [liveChatRecords, setLiveChatRecords] = useState([]);
  // Visitor ID records — isolated visitor_id_reports collection. Aggregate
  // demographic snapshots (no individual PII).
  const [visitorIdRecords, setVisitorIdRecords] = useState([]);
  // PMAX records: isolated pmax_reports collection, single-location clients only.
  const [pmaxRecords, setPmaxRecords] = useState([]);

  // Trailing 6-month GA4 sessions rollup for the RAAG sparkline. Independent
  // of the selected report period — always pulls the last 6 calendar months.
  const [analyticsMonthly, setAnalyticsMonthly] = useState([]);
  // GA4 "before/after" comparison powering the hero card in RAAG.
  // Backend returns has_comparison=false when there isn't enough baseline,
  // in which case the card silently hides.
  const [beforeAfter, setBeforeAfter] = useState(null);
  // Agency-written note for this client + report period. Surfaced as a
  // card at the top of RAAG (visible to clients).
  const [agencyNote, setAgencyNote] = useState({ body: "", updated_at: null });
  
  // CTV and Device metrics state
  const [ctvPublishers, setCtvPublishers] = useState([]);
  const [deviceMetrics, setDeviceMetrics] = useState([]);
  const [ctvAudiences, setCtvAudiences] = useState([]);
  const [ctvConversions, setCtvConversions] = useState([]);
  const [ctvMonths, setCtvMonths] = useState([]);
  
  // Data coverage (which months have imported data)
  const [dataCoverage, setDataCoverage] = useState({ months: [], periods: [] });
  
  const [expandedCampaigns, setExpandedCampaigns] = useState({});

  // Calculate brand channel impressions that overlap with report period
  const getBrandChannelImpressions = () => {
    if (!localClient.brand_channels || localClient.brand_channels.length === 0) return 0;
    const rpStart = localClient.report_start ? new Date(localClient.report_start + 'T00:00:00') : null;
    const rpEnd = localClient.report_end ? new Date(localClient.report_end + 'T00:00:00') : null;
    return localClient.brand_channels.reduce((sum, ch) => {
      const imp = parseInt(ch.impressions) || 0;
      if (imp === 0) return sum;
      if (!rpStart || !rpEnd) return sum + imp;
      if (!ch.report_start || !ch.report_end) return sum + imp;
      const chStart = new Date(ch.report_start + 'T00:00:00');
      const chEnd = new Date(ch.report_end + 'T00:00:00');
      if (chStart <= rpEnd && chEnd >= rpStart) return sum + imp;
      return sum;
    }, 0);
  };
  const brandChannelImpressions = getBrandChannelImpressions();

  // Filter brand channels to only show those overlapping with report period
  const filteredBrandChannels = (() => {
    if (!localClient.brand_channels || localClient.brand_channels.length === 0) return [];
    const rpStart = localClient.report_start ? new Date(localClient.report_start + 'T00:00:00') : null;
    const rpEnd = localClient.report_end ? new Date(localClient.report_end + 'T00:00:00') : null;
    if (!rpStart || !rpEnd) return localClient.brand_channels;
    return localClient.brand_channels.filter(ch => {
      if (!ch.report_start || !ch.report_end) return true;
      const chStart = new Date(ch.report_start + 'T00:00:00');
      const chEnd = new Date(ch.report_end + 'T00:00:00');
      return chStart <= rpEnd && chEnd >= rpStart;
    });
  })();

  // Meta campaign data state
  const [metaSummary, setMetaSummary] = useState(null);
  
  // Mobile campaign data state
  const [mobileSummary, setMobileSummary] = useState(null);
  
  // Social Mirror / Display Ads data state
  const [socialMirrorSummary, setSocialMirrorSummary] = useState(null);
  
  // Facebook campaign data state (from PDF imports)
  const [facebookCampaign, setFacebookCampaign] = useState(null);
  
  // App Front campaign data state
  const [appfrontData, setAppfrontData] = useState(null);
  
  // Google Ads campaign data state
  const [googleAdsData, setGoogleAdsData] = useState(null);

  // TikTok demographics data state
  const [tiktokDemographics, setTiktokDemographics] = useState([]);
  const [tiktokSummary, setTiktokSummary] = useState(null);

  // Ad creative links state
  const [adCreatives, setAdCreatives] = useState([]);
  const [previewCreative, setPreviewCreative] = useState(null);

  // Share link state
  const [shareLink, setShareLink] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  // Update localClient when client prop changes
  useEffect(() => {
    setLocalClient(client);
  }, [client]);

  const startEditing = (field) => {
    setEditingField(field);
    setEditValue(field === 'geo' ? (localClient.geo_targeting || '') : (localClient.audience_targeting || ''));
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  const saveField = async () => {
    setSavingField(true);
    try {
      const updateData = {
        name: localClient.name,
        industry: localClient.industry || "",
        contact_email: localClient.contact_email || "",
        platforms: localClient.platforms || [],
        geo_targeting: editingField === 'geo' ? editValue : (localClient.geo_targeting || ""),
        audience_targeting: editingField === 'audience' ? editValue : (localClient.audience_targeting || ""),
        report_start: localClient.report_start || "",
        report_end: localClient.report_end || "",
      };
      
      await axios.put(`${API}/clients/${localClient.id}`, updateData);
      
      // Update local state
      setLocalClient(prev => ({
        ...prev,
        geo_targeting: updateData.geo_targeting,
        audience_targeting: updateData.audience_targeting,
      }));
      
      toast.success("Campaign details updated");
      setEditingField(null);
      setEditValue("");
      
      // Trigger refresh in parent
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Error updating client:", e);
      toast.error("Failed to update campaign details");
    } finally {
      setSavingField(false);
    }
  };

  // Fetch metrics directly for this client
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const [metricsRes, adMetricsRes] = await Promise.all([
          axios.get(`${API}/metrics?client_id=${client.id}`),
          axios.get(`${API}/ad-metrics?client_id=${client.id}`)
        ]);
        setLocalMetrics(metricsRes.data);
        setLocalAdMetrics(adMetricsRes.data);
      } catch (e) {
        console.error("Error fetching client data:", e);
      }
    };
    fetchClientData();
  }, [client.id, API]);

  // Fetch top creatives
  const fetchTopCreatives = async () => {
    setLoadingCreatives(true);
    try {
      const res = await axios.get(`${API}/top-creatives?client_id=${client.id}&limit=3`);
      setTopCreatives(res.data);
    } catch (e) {
      console.error("Error fetching top creatives:", e);
    } finally {
      setLoadingCreatives(false);
    }
  };

  useEffect(() => {
    fetchTopCreatives();
  }, [client.id, API]);

  // Handle description editing
  const startEditingDescription = (adName, currentDescription) => {
    setEditingDescription(adName);
    setDescriptionValue(currentDescription || "");
  };

  const cancelEditingDescription = () => {
    setEditingDescription(null);
    setDescriptionValue("");
  };

  const saveDescription = async (adName) => {
    setSavingDescription(true);
    try {
      await axios.put(`${API}/creative-description`, null, {
        params: {
          client_id: client.id,
          ad_name: adName,
          description: descriptionValue
        }
      });
      
      toast.success("Description saved");
      setEditingDescription(null);
      setDescriptionValue("");
      fetchTopCreatives(); // Refresh to show updated description
    } catch (e) {
      console.error("Error saving description:", e);
      toast.error("Failed to save description");
    } finally {
      setSavingDescription(false);
    }
  };

  // Fetch marketing channels
  const fetchMarketingChannels = async () => {
    try {
      const res = await axios.get(`${API}/marketing-channels?client_id=${client.id}&active_only=false`);
      setMarketingChannels(res.data);
    } catch (e) {
      console.error("Error fetching marketing channels:", e);
    }
  };

  useEffect(() => {
    fetchMarketingChannels();
  }, [client.id, API]);

  // Fetch traffic acquisition data
  // Build report period query params for API calls
  const rpParams = localClient.report_start && localClient.report_end
    ? `&report_start=${encodeURIComponent(localClient.report_start)}&report_end=${encodeURIComponent(localClient.report_end)}`
    : '';
  
  // Location filter param for multi-location
  const locationParam = activeLocation && activeLocation !== "all"
    ? `&location_name=${encodeURIComponent(activeLocation)}`
    : '';

  // Fetch location summary for the tabs
  const fetchLocationSummary = async () => {
    try {
      const res = await axios.get(`${API}/location-summary?client_id=${client.id}`);
      setLocationSummary(res.data.locations || []);
    } catch (e) {
      console.error("Error fetching location summary:", e);
    }
  };

  useEffect(() => {
    if (client.client_type === 'multi') {
      fetchLocationSummary();
    }
  }, [client.id, API]);

  const fetchTrafficData = async () => {
    try {
      let url = `${API}/stats/traffic-acquisition?client_id=${client.id}`;
      if (localClient.report_start && localClient.report_end) {
        url += `&report_start=${localClient.report_start}&report_end=${localClient.report_end}`;
      } else if (dateRange.from && dateRange.to) {
        const fromStr = format(dateRange.from, "yyyy-MM-dd");
        const toStr = format(dateRange.to, "yyyy-MM-dd");
        url += `&start_date=${fromStr}&end_date=${toStr}`;
      }
      const res = await axios.get(url);
      setTrafficData(res.data);
    } catch (e) {
      console.error("Error fetching traffic data:", e);
    }
  };

  useEffect(() => {
    fetchTrafficData();
  }, [client.id, API, dateRange, localClient.report_start, localClient.report_end]);

  // Fetch trailing-6-month GA4 rollup for the RAAG sparkline. This is
  // intentionally NOT tied to the selected report period — the sparkline
  // always shows the last 6 calendar months regardless of which report
  // period the agency is viewing.
  useEffect(() => {
    let cancelled = false;
    const fetchMonthly = async () => {
      try {
        const res = await axios.get(`${API}/stats/analytics-monthly?client_id=${client.id}&months=60`);
        if (!cancelled) setAnalyticsMonthly(res.data || []);
      } catch (e) {
        if (!cancelled) setAnalyticsMonthly([]);
      }
    };
    fetchMonthly();
    return () => { cancelled = true; };
  }, [client.id, API]);

  // Content Effect fetch — pulls ALL periods so users can step through history.
  // The card itself picks the most recent period to display.
  useEffect(() => {
    let cancelled = false;
    const fetchCE = async () => {
      try {
        const res = await axios.get(`${API}/content-pulse/${client.id}`);
        if (!cancelled) setContentEffectRecords(res.data?.records || []);
      } catch (e) {
        if (!cancelled) setContentEffectRecords([]);
      }
    };
    fetchCE();
    return () => { cancelled = true; };
  }, [client.id, API]);

  // Live Chat fetch — pulls ALL periods, newest first. The card picks the
  // most recent and offers a period switcher when 2+ months exist.
  useEffect(() => {
    let cancelled = false;
    const fetchLC = async () => {
      try {
        const res = await axios.get(`${API}/live-chat/${client.id}`);
        if (!cancelled) setLiveChatRecords(res.data?.records || []);
      } catch (e) {
        if (!cancelled) setLiveChatRecords([]);
      }
    };
    fetchLC();
    return () => { cancelled = true; };
  }, [client.id, API]);

  // Visitor ID fetch — pulls all aggregate snapshots, newest first.
  useEffect(() => {
    let cancelled = false;
    const fetchVID = async () => {
      try {
        const res = await axios.get(`${API}/visitor-id/${client.id}`);
        if (!cancelled) setVisitorIdRecords(res.data?.records || []);
      } catch (e) {
        if (!cancelled) setVisitorIdRecords([]);
      }
    };
    fetchVID();
    return () => { cancelled = true; };
  }, [client.id, API]);

  useEffect(() => {
    let cancelled = false;
    if (client.client_type === "multi") { setPmaxRecords([]); return undefined; }
    axios.get(`${API}/pmax/${client.id}`)
      .then((res) => { if (!cancelled) setPmaxRecords(res.data?.records || []); })
      .catch(() => { if (!cancelled) setPmaxRecords([]); });
    return () => { cancelled = true; };
  }, [client.id, client.client_type, API]);

  // Fetch GA4 before/after comparison for the RAAG hero card.
  useEffect(() => {
    let cancelled = false;
    const fetchBA = async () => {
      try {
        const res = await axios.get(`${API}/stats/analytics-before-after?client_id=${client.id}&window_months=6`);
        if (!cancelled) setBeforeAfter(res.data || null);
      } catch (e) {
        if (!cancelled) setBeforeAfter(null);
      }
    };
    fetchBA();
    return () => { cancelled = true; };
  }, [client.id, API]);

  // Fetch the agency note for the current report period. Re-fires when
  // the period changes so each quarter shows its own dedicated note.
  useEffect(() => {
    let cancelled = false;
    const fetchNote = async () => {
      const rs = localClient.report_start;
      const re = localClient.report_end;
      if (!rs || !re) { setAgencyNote({ body: "", updated_at: null }); return; }
      try {
        const res = await axios.get(`${API}/agency-notes`, {
          params: { client_id: client.id, report_start: rs, report_end: re },
        });
        if (!cancelled) setAgencyNote(res.data || { body: "", updated_at: null });
      } catch (e) {
        if (!cancelled) setAgencyNote({ body: "", updated_at: null });
      }
    };
    fetchNote();
    return () => { cancelled = true; };
  }, [client.id, API, localClient.report_start, localClient.report_end]);

  const saveAgencyNote = async (body) => {
    try {
      const res = await axios.put(`${API}/agency-notes`, {
        client_id: client.id,
        report_start: agencyNote.report_start || localClient.report_start,
        report_end: agencyNote.report_end || localClient.report_end,
        body,
      });
      setAgencyNote(res.data || { body: "", updated_at: null });
      toast.success(body ? "Note saved" : "Note cleared");
    } catch (e) {
      toast.error("Couldn't save note");
    }
  };

  // Fetch CTV publishers and device metrics
  const fetchCtvData = async () => {
    try {
      const rpParams = hasReportPeriod ? `&report_start=${localClient.report_start}&report_end=${localClient.report_end}` : '';
      const [pubRes, devRes, audRes, convRes, monthRes] = await Promise.all([
        axios.get(`${API}/ctv-publishers?client_id=${client.id}${rpParams}${locationParam}`),
        axios.get(`${API}/device-metrics?client_id=${client.id}${rpParams}${locationParam}`),
        axios.get(`${API}/ctv-audiences?client_id=${client.id}${rpParams}${locationParam}`),
        axios.get(`${API}/ctv-conversions?client_id=${client.id}${rpParams}${locationParam}`),
        axios.get(`${API}/ctv-months?client_id=${client.id}${rpParams}${locationParam}`)
      ]);
      setCtvPublishers(pubRes.data);
      setDeviceMetrics(devRes.data);
      setCtvAudiences(audRes.data);
      setCtvConversions(convRes.data);
      setCtvMonths(monthRes.data);
    } catch (e) {
      console.error("Error fetching CTV data:", e);
    }
  };

  // Fetch Meta campaign summary
  const fetchMetaSummary = async () => {
    try {
      const res = await axios.get(`${API}/meta-summary?client_id=${client.id}${rpParams}${locationParam}`);
      setMetaSummary(res.data);
    } catch (e) {
      console.error("Error fetching Meta summary:", e);
    }
  };

  // Fetch Mobile campaign summary
  const fetchMobileSummary = async () => {
    try {
      const res = await axios.get(`${API}/mobile-summary?client_id=${client.id}${rpParams}${locationParam}`);
      setMobileSummary(res.data);
    } catch (e) {
      console.error("Error fetching Mobile summary:", e);
    }
  };

  // Fetch Social Mirror / Display Ads summary
  const fetchSocialMirrorSummary = async () => {
    try {
      const res = await axios.get(`${API}/social-mirror-summary?client_id=${client.id}${rpParams}${locationParam}`);
      setSocialMirrorSummary(res.data);
    } catch (e) {
      console.error("Error fetching Social Mirror summary:", e);
    }
  };

  // Fetch Facebook campaign summary (from PDF imports)
  const fetchFacebookCampaign = async () => {
    try {
      const res = await axios.get(`${API}/facebook-campaign-summary?client_id=${client.id}${rpParams}${locationParam}`);
      if (res.data.has_data) {
        setFacebookCampaign(res.data);
      } else {
        setFacebookCampaign(null);
      }
    } catch (e) {
      console.error("Error fetching Facebook campaign:", e);
      setFacebookCampaign(null);
    }
  };

  // Fetch App Front campaign data
  const fetchAppfrontData = async () => {
    try {
      const res = await axios.get(`${API}/appfront-summary?client_id=${client.id}${rpParams}`);
      setAppfrontData(res.data);
    } catch (e) {
      console.error("Error fetching App Front data:", e);
    }
  };

  // Fetch Google Ads campaign data
  const fetchGoogleAdsData = async () => {
    try {
      const res = await axios.get(`${API}/google-ads-summary?client_id=${client.id}${rpParams}${locationParam}`);
      setGoogleAdsData(res.data);
    } catch (e) {
      console.error("Error fetching Google Ads data:", e);
    }
  };

  // Fetch TikTok demographics data
  const fetchTiktokDemographics = async () => {
    try {
      const res = await axios.get(`${API}/tiktok-demographics?client_id=${client.id}${rpParams}`);
      setTiktokDemographics(res.data);
    } catch (e) {
      console.error("Error fetching TikTok demographics:", e);
    }
  };

  // Fetch TikTok comprehensive summary
  const fetchTiktokSummary = async () => {
    try {
      const res = await axios.get(`${API}/tiktok-summary?client_id=${client.id}${rpParams}${locationParam}`);
      setTiktokSummary(res.data);
    } catch (e) {
      console.error("Error fetching TikTok summary:", e);
    }
  };

  const fetchAdCreatives = async () => {
    try {
      const res = await axios.get(`${API}/ad-creatives?client_id=${client.id}`);
      setAdCreatives(res.data);
    } catch (e) {
      console.error("Error fetching ad creatives:", e);
    }
  };

  // Fetch share link status
  const fetchShareLink = async () => {
    try {
      const res = await axios.get(`${API}/share-links?client_id=${client.id}`);
      if (res.data && res.data.token) setShareLink(res.data);
    } catch (e) {}
  };

  const generateShareLink = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/share-links`, { client_id: client.id });
      setShareLink(res.data);
    } catch (e) {
      console.error("Error generating share link:", e);
    } finally {
      setShareLoading(false);
    }
  };

  const toggleShareLink = async (active) => {
    if (!shareLink) return;
    try {
      const res = await axios.put(`${API}/share-links/${shareLink.token}`, { is_active: active });
      setShareLink(res.data);
    } catch (e) {}
  };

  const updateShareExpiry = async (expiresAt) => {
    if (!shareLink) return;
    try {
      const res = await axios.put(`${API}/share-links/${shareLink.token}`, { expires_at: expiresAt });
      setShareLink(res.data);
    } catch (e) {}
  };

  const deleteShareLink = async () => {
    if (!shareLink) return;
    try {
      await axios.delete(`${API}/share-links/${shareLink.token}`);
      setShareLink(null);
      setShowShareModal(false);
    } catch (e) {}
  };

  useEffect(() => {
    // Clear all summary states when report period changes to prevent stale data
    setFacebookCampaign(null);
    setMetaSummary(null);
    setMobileSummary(null);
    setSocialMirrorSummary(null);
    setGoogleAdsData(null);
    setAppfrontData(null);
    setTiktokDemographics([]);
    setCtvPublishers([]);
    setDeviceMetrics([]);
    setCtvAudiences([]);
    setCtvConversions([]);
    setCtvMonths([]);
    setLocalMetrics([]);
    setLocalAdMetrics([]);

    fetchCtvData();
    fetchMetaSummary();
    fetchMobileSummary();
    fetchSocialMirrorSummary();
    fetchFacebookCampaign();
    fetchAppfrontData();
    fetchGoogleAdsData();
    fetchTiktokSummary();
    fetchAdCreatives();
    if (!readOnly) fetchShareLink();
    // Fetch metrics/ad-metrics/analytics with report period filter
    const fetchFiltered = async () => {
      try {
        const [mRes, aRes, anRes] = await Promise.all([
          axios.get(`${API}/metrics?client_id=${client.id}${rpParams}${locationParam}`),
          axios.get(`${API}/ad-metrics?client_id=${client.id}${rpParams}${locationParam}`),
          axios.get(`${API}/stats/analytics-summary?client_id=${client.id}${rpParams}`),
        ]);
        setLocalMetrics(mRes.data);
        setLocalAdMetrics(aRes.data);
        setAnalyticsData(anRes.data);
      } catch(e) {}
    };
    fetchFiltered();
    // Fetch data coverage (which months have data)
    const fetchCoverage = async () => {
      try {
        const res = await axios.get(`${API}/data-coverage?client_id=${client.id}`);
        setDataCoverage(res.data);
      } catch(e) {}
    };
    fetchCoverage();
  }, [client.id, API, localClient.report_start, localClient.report_end, activeLocation]);

  // Memoize active metrics to prevent infinite loops
  const activeMetrics = useMemo(() => {
    return localMetrics.length > 0 ? localMetrics : metrics.filter(m => m.client_id === client.id);
  }, [localMetrics, metrics, client.id]);
  
  const activeAdMetrics = useMemo(() => {
    return localAdMetrics.length > 0 ? localAdMetrics : adMetrics.filter(m => m.client_id === client.id);
  }, [localAdMetrics, adMetrics, client.id]);

  // Normalize ad/creative names for matching — strips the noise that differs
  // between creative file names ("ClientName_AdSetName 320x50.gif") and ad-set
  // labels in the report ("Valley Roads"). After normalization both sides
  // should compare cleanly via exact match.
  const normalizeAdName = (s) => {
    if (!s) return "";
    return s
      .toLowerCase()
      .replace(/\.(gif|png|jpg|jpeg|mp4|mov|webp|webm)\b/g, "")        // file extensions
      .replace(/\b(320x50|728x90|300x250|320x480|160x600|970x250)\b/g, "") // common banner sizes
      .replace(/\b(scroller|video|display)\b/g, "")
      .replace(/\bcopy\s*\d*\b/g, "")                                   // "copy 2" suffixes
      .replace(/\(\d+\)/g, "")                                          // "(1)" "(2)"
      .replace(/[\s_\-.]+/g, " ")                                       // collapse separators (underscore, dash, dot, whitespace) → single space
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  // Helper to get creative for a campaign type, optionally matching a specific ad name.
  //
  // Match priority (most specific first):
  //   1. Exact match (case-insensitive, trimmed) on ad_name
  //   2. Normalized exact match — strips file extensions, banner sizes, separators
  //      so "Valley Roads" set matches "ClientName_Valley Roads 320x50.gif"
  //   3. Normalized "contains" with longest-overlap tiebreak — for cases where
  //      one normalized name is a strict subset of the other
  //   Otherwise: return undefined (no silent random fallback)
  //
  // When `adName` is NOT provided (section-header click):
  //   4. Campaign-level creative (no ad_name set)
  //   5. Any creative of this type as a last resort
  const getCreativeForType = (type, adName = null) => {
    const poolForType = adCreatives.filter(c => c.campaign_type === type);

    if (adName) {
      const needle = adName.trim().toLowerCase();

      // 1. Exact match (case + whitespace tolerant)
      const exact = poolForType.find(c => c.ad_name && c.ad_name.trim().toLowerCase() === needle);
      if (exact) return exact;

      // 2. Normalized exact match
      const normNeedle = normalizeAdName(adName);
      if (normNeedle) {
        const normExact = poolForType.find(c => c.ad_name && normalizeAdName(c.ad_name) === normNeedle);
        if (normExact) return normExact;

        // 3. Normalized "contains" with closest-overlap tiebreak. Among all
        //    candidates whose normalized name contains the needle (or vice
        //    versa), prefer the one whose normalized length is closest to
        //    the needle's length. This avoids the "Valley Roads" set name
        //    accidentally picking up the "Valley Roads (Close to Crozet)"
        //    creative when both contain "Valley Roads".
        const candidates = poolForType
          .filter(c => c.ad_name)
          .map(c => ({ c, n: normalizeAdName(c.ad_name) }))
          .filter(x => x.n && (x.n.includes(normNeedle) || normNeedle.includes(x.n)))
          .sort((a, b) => Math.abs(a.n.length - normNeedle.length) - Math.abs(b.n.length - normNeedle.length));
        if (candidates.length > 0) return candidates[0].c;
      }

      // adName was provided but no match — return undefined (explicit "no creative")
      return undefined;
    }

    // No adName given — section-header style click. Prefer campaign-level
    // creative (ad_name unset), otherwise any creative of this type.
    return poolForType.find(c => !c.ad_name) || poolForType[0] || undefined;
  };

  // Merge explicit target_zipcodes with any zip codes found in geo_targeting text
  const allTargetZipcodes = useMemo(() => {
    const zips = new Set(localClient.target_zipcodes || []);
    if (localClient.geo_targeting) {
      const found = localClient.geo_targeting.match(/\b\d{5}\b/g);
      if (found) found.forEach(z => zips.add(z));
    }
    return [...zips];
  }, [localClient.target_zipcodes, localClient.geo_targeting]);

  // TikTok totals for top-level stats
  const pmaxInRange = useMemo(
    () => pmaxInPeriod(pmaxRecords, localClient.report_start, localClient.report_end),
    [pmaxRecords, localClient.report_start, localClient.report_end],
  );
  const pmaxSum = useMemo(() => pmaxTotals(pmaxInRange), [pmaxInRange]);
  const previousPeriod = usePreviousPeriod({
    API, clientId: client.id, start: localClient.report_start, end: localClient.report_end,
    locationParam, brandChannels: localClient.brand_channels, pmaxRecords,
  });
  const [payoffNote, savePayoffNote] = useAgencyNote(API, client.id, localClient.report_start, localClient.report_end, "payoff");
  const geofenceLocs = useMemo(() => mobileSummary?.geofencing_locations || [], [mobileSummary]);
  const [mapTypes, setMapTypes] = useState(new Set());
  const channelStories = useMemo(() => buildChannelStories({
    clientName: localClient.name,
    metaOverview: facebookCampaign?.has_data ? facebookCampaign.overview : metaSummary?.overview,
    mobileOverview: mobileSummary?.overview,
    smOverview: socialMirrorSummary?.overview,
    tiktokOverview: tiktokSummary?.has_data ? tiktokSummary.overview : null,
    googleSummary: googleAdsData?.summary,
    pmax: pmaxSum,
    appfrontSummary: appfrontData?.summary,
    ctvPublishers,
  }), [localClient.name, facebookCampaign, metaSummary, mobileSummary, socialMirrorSummary, tiktokSummary, googleAdsData, pmaxSum, appfrontData, ctvPublishers]);
  const latestDataMonth = dataCoverage.months.length
    ? (() => { const [y, m] = dataCoverage.months[dataCoverage.months.length - 1].split('-'); return new Date(+y, +m - 1).toLocaleString('en', { month: 'long', year: 'numeric' }); })()
    : null;

  const tiktokTotals = useMemo(() => {
    const ads = filteredAdMetrics.filter(a => a.platform === 'tiktok');
    return {
      impressions: ads.reduce((s, a) => s + (a.impressions || 0), 0),
      reach: ads.reduce((s, a) => s + (a.reach || 0), 0),
      clicks: ads.reduce((s, a) => s + (a.clicks || 0), 0),
    };
  }, [filteredAdMetrics]);

  // Report period comes from editable client fields, not auto-computed
  const hasReportPeriod = localClient.report_start && localClient.report_end;

  // Out-of-range months: imported data sits outside the client's current report window.
  // Catches the "I uploaded April data but my report period is Q1" mistake.
  const outOfRangeMonths = useMemo(() => {
    if (!hasReportPeriod || !dataCoverage.months?.length) return [];
    return dataCoverage.months.filter(month => {
      const inRange = localClient.report_start <= month + '-31' && localClient.report_end >= month + '-01';
      return !inRange;
    });
  }, [dataCoverage.months, hasReportPeriod, localClient.report_start, localClient.report_end]);

  // Suggested widened range that covers every imported month + the current window.
  const suggestedRange = useMemo(() => {
    if (outOfRangeMonths.length === 0) return null;
    const all = [...(dataCoverage.months || [])].sort();
    if (!all.length) return null;
    const firstMonth = all[0];            // YYYY-MM
    const lastMonth = all[all.length - 1]; // YYYY-MM
    const newStart = firstMonth + '-01';
    // Last day of last month
    const [ly, lm] = lastMonth.split('-').map(Number);
    const lastDay = new Date(ly, lm, 0).getDate();
    const newEnd = `${lastMonth}-${String(lastDay).padStart(2, '0')}`;
    // Keep whichever endpoint is wider so we never shrink the existing window.
    const start = (localClient.report_start && localClient.report_start < newStart) ? localClient.report_start : newStart;
    const end = (localClient.report_end && localClient.report_end > newEnd) ? localClient.report_end : newEnd;
    return { start, end };
  }, [outOfRangeMonths, dataCoverage.months, localClient.report_start, localClient.report_end]);

  const applySuggestedRange = async () => {
    if (!suggestedRange) return;
    try {
      await axios.put(`${API}/clients/${localClient.id}`, {
        name: localClient.name,
        industry: localClient.industry || "",
        contact_email: localClient.contact_email || "",
        platforms: localClient.platforms || [],
        geo_targeting: localClient.geo_targeting || "",
        audience_targeting: localClient.audience_targeting || "",
        report_start: suggestedRange.start,
        report_end: suggestedRange.end,
      });
      setLocalClient(prev => ({ ...prev, report_start: suggestedRange.start, report_end: suggestedRange.end }));
      toast.success(`Report period widened to cover all imported data`);
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Error widening report period:", e);
      toast.error("Failed to update report period");
    }
  };

  const ViewCreativeButton = ({ campaignType, adName = null, className = "" }) => {
    const creative = getCreativeForType(campaignType, adName);
    return (
      <button
        onClick={() => {
          if (creative) {
            setPreviewCreative(creative);
          } else if (onViewChange) {
            onViewChange("ad-creative");
          }
        }}
        className={`flex items-center gap-1.5 ${creative ? 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white border-white/20 hover:border-white/30' : 'bg-white/5 hover:bg-white/10 text-slate-500 hover:text-slate-300 border-white/10 hover:border-white/20'} text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${className}`}
        data-testid={`view-creative-${campaignType}`}
        title="View Creative"
      >
        <Eye className="w-3.5 h-3.5" />
        View Creative
      </button>
    );
  };

  const CreativeIcon = ({ campaignType, adName = null }) => {
    const creative = getCreativeForType(campaignType, adName);
    if (!creative && readOnly) return null;
    return (
      <button
        onClick={() => {
          if (creative) {
            setPreviewCreative(creative);
          } else if (onViewChange) {
            onViewChange("ad-creative");
          }
        }}
        className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${creative ? 'border-violet-400/40 bg-violet-400/10 text-violet-200 hover:bg-violet-400/25 hover:text-white' : 'border-white/10 text-slate-500 hover:text-slate-300'}`}
        title="See the ad"
        data-testid={`see-ad-${campaignType}-${(adName || "main").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <Eye className="w-3.5 h-3.5" />
        See ad
      </button>
    );
  };

  useEffect(() => {
    let filtered = activeMetrics;
    
    const filterStart = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : localClient.report_start;
    const filterEnd = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : localClient.report_end;
    
    if (filterStart && filterEnd) {
      filtered = filtered.filter((m) => {
        if (m.date) return m.date >= filterStart && m.date <= filterEnd;
        if (m.report_start && m.report_end) return m.report_start <= filterEnd && m.report_end >= filterStart;
        return true;
      });
    }
    
    setFilteredMetrics(filtered);

    // Calculate stats
    const totalImpressions = filtered.reduce((sum, m) => sum + m.impressions, 0);
    const totalClicks = filtered.reduce((sum, m) => sum + m.clicks, 0);
    const totalSpend = filtered.reduce((sum, m) => sum + m.spend, 0);
    const totalLikes = filtered.reduce((sum, m) => sum + m.likes, 0);
    const totalComments = filtered.reduce((sum, m) => sum + m.comments, 0);
    const totalShares = filtered.reduce((sum, m) => sum + m.shares, 0);
    const totalFollowers = filtered.reduce((sum, m) => sum + m.new_followers, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    setStats({
      impressions: totalImpressions,
      clicks: totalClicks,
      ctr: avgCtr.toFixed(2),
      spend: totalSpend,
      likes: totalLikes,
      comments: totalComments,
      shares: totalShares,
      new_followers: totalFollowers,
    });
  }, [activeMetrics, dateRange]);

  useEffect(() => {
    let filtered = activeAdMetrics;
    
    const filterStart = dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : localClient.report_start;
    const filterEnd = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : localClient.report_end;
    
    if (filterStart && filterEnd) {
      filtered = filtered.filter((m) => {
        if (m.date) return m.date >= filterStart && m.date <= filterEnd;
        if (m.report_start && m.report_end) return m.report_start <= filterEnd && m.report_end >= filterStart;
        return true;
      });
    }
    
    setFilteredAdMetrics(filtered);
  }, [activeAdMetrics, dateRange, localClient.report_start, localClient.report_end]);

  const generateInsight = async () => {
    setLoadingInsight(true);
    try {
      const metricsSummary = `
        Total Impressions: ${stats.impressions.toLocaleString()}
        Total Clicks: ${stats.clicks.toLocaleString()}
        Average CTR: ${stats.ctr}%
        Total Spend: $${stats.spend.toLocaleString()}
        Total Likes: ${stats.likes.toLocaleString()}
        Total Comments: ${stats.comments.toLocaleString()}
        Total Shares: ${stats.shares.toLocaleString()}
        New Followers: ${stats.new_followers.toLocaleString()}
        Total Engagement: ${(stats.likes + stats.comments + stats.shares).toLocaleString()}
        Platforms: ${client.platforms?.join(", ") || "N/A"}
        Industry: ${client.industry || "N/A"}
      `;

      const response = await axios.post(`${API}/insights`, {
        client_id: client.id,
        metrics_summary: metricsSummary,
      });

      setInsight(response.data);
      toast.success("AI insights generated!");
    } catch (e) {
      console.error("Error generating insight:", e);
      toast.error("Failed to generate insights");
    } finally {
      setLoadingInsight(false);
    }
  };

  // Prepare chart data
  const chartData = filteredMetrics
    .slice(0, 30)
    .reverse()
    .map((m) => ({
      date: m.date,
      impressions: m.impressions,
      clicks: m.clicks,
      engagement: m.likes + m.comments + m.shares,
    }));

  // Prepare ad performance data
  const adChartData = filteredAdMetrics.map((ad) => ({
    name: ad.ad_name.length > 15 ? ad.ad_name.substring(0, 15) + "..." : ad.ad_name,
    impressions: ad.impressions,
    clicks: ad.clicks,
    ctr: ad.ctr,
  }));

  // Calculate platform breakdown
  const platformBreakdown = useMemo(() => {
    const platforms = {};
    
    // Aggregate metrics by platform
    filteredMetrics.forEach((m) => {
      const platform = m.platform || 'unknown';
      if (!platforms[platform]) {
        platforms[platform] = { impressions: 0, clicks: 0, spend: 0, reach: 0 };
      }
      platforms[platform].impressions += m.impressions || 0;
      platforms[platform].clicks += m.clicks || 0;
      platforms[platform].spend += m.spend || 0;
      platforms[platform].reach += m.reach || 0;
    });
    
    // Also include ad metrics spend if not already counted
    filteredAdMetrics.forEach((ad) => {
      const platform = ad.platform || 'unknown';
      if (!platforms[platform]) {
        platforms[platform] = { impressions: 0, clicks: 0, spend: 0, reach: 0 };
      }
    });
    
    // Calculate CTR for each platform
    return Object.entries(platforms).map(([platform, data]) => ({
      platform,
      ...data,
      ctr: data.impressions > 0 ? (data.clicks / data.impressions * 100) : 0
    }));
  }, [filteredMetrics, filteredAdMetrics]);

  // Group ad metrics by platform
  const adsByPlatform = useMemo(() => {
    const grouped = {};
    filteredAdMetrics.forEach((ad) => {
      const platform = ad.platform || 'unknown';
      if (!grouped[platform]) {
        grouped[platform] = [];
      }
      grouped[platform].push(ad);
    });
    return grouped;
  }, [filteredAdMetrics]);

  const platformColors = {
    facebook: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
    instagram: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', badge: 'bg-pink-100 text-pink-700' },
    tiktok: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-700' },
    google: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
    unknown: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-700' },
  };

  return (
    <div className="space-y-6" data-testid="client-detail">
      {/* Ad Preview Modal */}
      {previewCreative && (
        <AdPreviewModal
          creative={previewCreative}
          onClose={() => setPreviewCreative(null)}
        />
      )}

      {/* Share Report Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-2xl p-5 md:p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-slate-900 font-['Outfit']">Share Report</h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            {!shareLink ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-4">
                  <Link className="w-7 h-7 text-violet-500" />
                </div>
                <p className="text-slate-600 mb-4 text-sm">Generate a shareable link for <strong>{client.name}</strong>'s report. Anyone with the link can view it.</p>
                <Button
                  onClick={generateShareLink}
                  disabled={shareLoading}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6"
                  data-testid="generate-link-btn"
                >
                  {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link className="w-4 h-4 mr-2" />}
                  Generate Link
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Link URL */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 block">Report Link</label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/report/${shareLink.token}`}
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 font-mono"
                      data-testid="share-link-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      data-testid="copy-link-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/report/${shareLink.token}`);
                        toast.success("Link copied to clipboard!");
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <a
                      href={`/report/${shareLink.token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50"
                      data-testid="preview-link-btn"
                    >
                      <ExternalLink className="w-4 h-4 text-slate-600" />
                    </a>
                  </div>
                </div>

                {/* Status toggle */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Link Status</p>
                    <p className="text-xs text-slate-400">{shareLink.is_active ? "Anyone with the link can view" : "Link is deactivated"}</p>
                  </div>
                  <button
                    onClick={() => toggleShareLink(!shareLink.is_active)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${shareLink.is_active ? 'bg-green-500' : 'bg-slate-300'}`}
                    data-testid="toggle-link-btn"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${shareLink.is_active ? 'translate-x-5' : ''}`} />
                  </button>
                </div>

                {/* Expiration */}
                <div className="flex items-center justify-between py-2 border-t border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Expiration</p>
                    <p className="text-xs text-slate-400">
                      {shareLink.expires_at ? `Expires ${new Date(shareLink.expires_at).toLocaleDateString()}` : "No expiration set"}
                    </p>
                  </div>
                  <select
                    className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-slate-600"
                    data-testid="expiry-select"
                    value=""
                    onChange={(e) => {
                      const days = parseInt(e.target.value);
                      if (days === 0) {
                        updateShareExpiry("");
                      } else {
                        const d = new Date();
                        d.setDate(d.getDate() + days);
                        updateShareExpiry(d.toISOString());
                      }
                    }}
                  >
                    <option value="" disabled>Set expiry</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="0">Never</option>
                  </select>
                </div>

                {/* Delete link */}
                <div className="pt-2 border-t border-slate-100">
                  <button
                    onClick={deleteShareLink}
                    className="text-sm text-red-500 hover:text-red-600 font-medium"
                    data-testid="delete-link-btn"
                  >
                    Delete link permanently
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        {!readOnly && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="p-2 rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-3 md:gap-4">
            {localClient.logo_url ? (
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm" data-testid="client-logo">
                <img
                  src={localClient.logo_url}
                  alt={`${localClient.name} logo`}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-lime-400 flex items-center justify-center text-slate-900 font-black text-lg md:text-xl shadow-lg shadow-cyan-500/20 flex-shrink-0">
                {client.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Outfit'] truncate">
                {client.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {client.industry && (
                  <span className="text-sm text-slate-500">{client.industry}</span>
                )}
                <div className="flex gap-2">
                  {client.platforms?.includes("facebook") && (
                    <Facebook className="w-4 h-4 text-blue-600" />
                  )}
                  {client.platforms?.includes("instagram") && (
                    <Instagram className="w-4 h-4 text-pink-600" />
                  )}
                  {client.platforms?.includes("tiktok") && (
                    <span className="text-black">
                      <TikTokIcon />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Date Range Badge */}
        {hideDatePill ? null : hasReportPeriod && readOnly ? (
          <div className="flex items-center gap-2 bg-white rounded-full px-4 md:px-5 py-2 md:py-2.5 border border-cyan-200 shadow-sm" data-testid="report-date-range">
            <Calendar className="w-4 h-4 md:w-5 md:h-5 text-cyan-600 flex-shrink-0" />
            <span className="text-sm md:text-base font-semibold text-slate-800">
              {friendlyRange(localClient.report_start, localClient.report_end)}
            </span>
          </div>
        ) : hasReportPeriod ? (
          <button
            type="button"
            onClick={() => setEditingField('report_period')}
            className="group flex items-center gap-2 bg-white rounded-full pl-4 md:pl-5 pr-1.5 py-1.5 border border-cyan-200 shadow-sm hover:border-cyan-400 hover:shadow transition-colors"
            data-testid="report-date-range"
          >
            <Calendar className="w-4 h-4 md:w-5 md:h-5 text-cyan-600 flex-shrink-0" />
            <span className="text-sm md:text-base font-semibold text-slate-800">
              {friendlyRange(localClient.report_start, localClient.report_end)}
            </span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-cyan-50 group-hover:bg-cyan-600 group-hover:text-white text-cyan-700 text-xs font-semibold px-3 py-1 transition-colors" data-testid="change-dates-btn">
              <Pencil className="w-3 h-3" /> Change dates
            </span>
          </button>
        ) : !readOnly ? (
          <button
            onClick={() => setEditingField('report_period')}
            className="flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 border border-slate-200 border-dashed text-slate-400 hover:text-slate-600 hover:border-slate-300 text-sm"
            data-testid="set-report-period-btn"
          >
            <Calendar className="w-4 h-4" />
            Set Report Period
          </button>
        ) : null}

        {/* Data Coverage Indicator */}
        {dataCoverage.months.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-2xl px-4 py-2 border border-slate-200 max-w-full" data-testid="data-coverage-indicator">
            <span className="text-xs font-medium text-slate-500">Data available:</span>
            <div className="flex flex-wrap items-center gap-1">
              {dataCoverage.months.map(month => {
                const [y, m] = month.split('-');
                const label = new Date(parseInt(y), parseInt(m) - 1).toLocaleString('en', { month: 'short' });
                const isInRange = hasReportPeriod && localClient.report_start <= month + '-31' && localClient.report_end >= month + '-01';
                return (
                  <span
                    key={month}
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isInRange ? 'bg-lime-100 text-lime-800 border border-lime-300' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}
                    title={`${label} ${y}${isInRange ? ' (in report period)' : ' (outside report period, not shown in hero totals)'}`}
                  >
                    {label}{dataCoverage.months.some(mm => mm.slice(0, 4) !== y) ? ` ${y.slice(2)}` : ""}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {latestDataMonth && (
          <div className="flex items-center gap-1.5 bg-lime-50 rounded-full px-3 py-2 border border-lime-200" data-testid="data-freshness-note">
            <span className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
            <span className="text-xs font-semibold text-lime-900">Updated through {latestDataMonth}</span>
          </div>
        )}

        {/* Out-of-range warning — catches "I imported April but my period is Q1" */}
        {!readOnly && outOfRangeMonths.length > 0 && suggestedRange && (
          <button
            type="button"
            onClick={applySuggestedRange}
            className="group flex items-center gap-2 bg-amber-50 hover:bg-amber-100 rounded-full px-3 md:px-4 py-2 border border-amber-300 hover:border-amber-400 transition"
            data-testid="out-of-range-warning"
            title={`Imported months outside this client's report period (${outOfRangeMonths.join(', ')}). Click to widen the period to ${suggestedRange.start} → ${suggestedRange.end}.`}
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-900">
              {outOfRangeMonths.length} month{outOfRangeMonths.length === 1 ? '' : 's'} of data outside report period
            </span>
            <span className="text-[11px] font-medium text-amber-700 hidden sm:inline group-hover:text-amber-900">
              · Click to widen
            </span>
          </button>
        )}

        {/* Logo URL Badge — agency-facing; shown only when editing is enabled */}
        {!readOnly && (
          <div className="flex items-center gap-2 bg-cyan-50 rounded-full px-3 md:px-4 py-2 border border-cyan-200" data-testid="logo-url-badge">
            <span className="text-xs text-slate-500">Logo:</span>
            <span className="text-xs md:text-sm font-semibold text-slate-700 truncate max-w-[140px]" data-testid="logo-url-value">
              {localClient.logo_url ? "Set" : "Not set"}
            </span>
            <button
              type="button"
              onClick={() => { setEditingField('logo_url'); setEditValue(localClient.logo_url || ""); }}
              className="ml-1 text-slate-400 hover:text-slate-600 flex-shrink-0"
              data-testid="edit-logo-url-btn"
              title="Paste a public URL to the client's logo image"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Logo URL Editor Modal */}
        {editingField === 'logo_url' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingField(null)} data-testid="logo-url-modal">
            <div className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-slate-700 mb-1">Logo URL for {localClient.name}</h3>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Paste a public URL to the client's logo image (PNG or SVG works best, transparent background preferred).
                Shown in the report header instead of the default gradient letter.
              </p>
              <input
                type="text"
                placeholder="https://example.com/logo.png"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                data-testid="logo-url-input"
              />
              {editValue && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-[11px] text-slate-500 mb-2 font-semibold uppercase tracking-wider">Preview</p>
                  <div className="w-14 h-14 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center overflow-hidden shadow-sm">
                    <img src={editValue} alt="preview" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button
                  size="sm"
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg"
                  onClick={async () => {
                    const url = editValue.trim();
                    try {
                      await axios.put(`${API}/clients/${client.id}`, {
                        name: localClient.name,
                        industry: localClient.industry || "",
                        contact_email: localClient.contact_email || "",
                        platforms: localClient.platforms || [],
                        geo_targeting: localClient.geo_targeting || "",
                        audience_targeting: localClient.audience_targeting || "",
                        report_start: localClient.report_start || "",
                        report_end: localClient.report_end || "",
                        logo_url: url,
                      });
                      setLocalClient(prev => ({ ...prev, logo_url: url }));
                      setEditingField(null);
                      toast.success(url ? "Logo saved" : "Logo cleared");
                    } catch(e) { toast.error("Failed to update"); }
                  }}
                  data-testid="save-logo-url-btn"
                >
                  <Check className="w-4 h-4 mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setEditingField(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Close Rate Editor Modal */}
        {editingField === 'close_rate' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditingField(null)} data-testid="close-rate-modal">
            <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-slate-700 mb-1">Close rate for {localClient.name}</h3>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                The percentage of website visits that become paying customers. Used on the "Est. New Customers" tile.
                Industry average is <strong>2.0%</strong>. Tune this to your client's actual history if you know it.
              </p>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="100"
                  className="w-full border border-slate-200 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  data-testid="close-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  size="sm"
                  className="flex-1 bg-lime-600 hover:bg-lime-700 text-white rounded-lg"
                  onClick={async () => {
                    const rate = parseFloat(editValue);
                    if (isNaN(rate) || rate <= 0 || rate > 100) {
                      toast.error("Enter a number between 0.1 and 100");
                      return;
                    }
                    try {
                      await axios.put(`${API}/clients/${client.id}`, {
                        name: localClient.name,
                        industry: localClient.industry || "",
                        contact_email: localClient.contact_email || "",
                        platforms: localClient.platforms || [],
                        geo_targeting: localClient.geo_targeting || "",
                        audience_targeting: localClient.audience_targeting || "",
                        report_start: localClient.report_start || "",
                        report_end: localClient.report_end || "",
                        close_rate_pct: rate,
                      });
                      setLocalClient(prev => ({ ...prev, close_rate_pct: rate }));
                      setEditingField(null);
                      toast.success(`Close rate set to ${rate}%`);
                    } catch(e) { toast.error("Failed to update"); }
                  }}
                  data-testid="save-close-rate-btn"
                >
                  <Check className="w-4 h-4 mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setEditingField(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        )}

        {/* Report Period Editor */}
        {editingField === 'report_period' && (
          <ReportPeriodEditor
            start={localClient.report_start}
            end={localClient.report_end}
            months={dataCoverage.months}
            onClose={() => setEditingField(null)}
            onSave={async (start, end) => {
              try {
                await axios.put(`${API}/clients/${client.id}`, {
                  name: localClient.name,
                  industry: localClient.industry || "",
                  contact_email: localClient.contact_email || "",
                  platforms: localClient.platforms || [],
                  geo_targeting: localClient.geo_targeting || "",
                  audience_targeting: localClient.audience_targeting || "",
                  report_start: start,
                  report_end: end,
                });
                setLocalClient(prev => ({ ...prev, report_start: start, report_end: end }));
                setEditingField(null);
                toast.success("Report dates updated");
              } catch (e) { toast.error("Failed to update"); }
            }}
          />
        )}

        {/* Date Range Picker - Removed, using Report Period instead */}
        {!readOnly && (
        <Button
          variant="outline"
          className="rounded-full"
          data-testid="share-report-btn"
          onClick={() => setShowShareModal(true)}
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share Report
        </Button>
        )}
      </div>

      {/* MULTI-LOCATION TABS */}
      {(localClient.locations?.length > 0 || locationSummary.length > 0) && (
        <div className="mb-6" data-testid="location-tabs">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setActiveLocation("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeLocation === "all"
                  ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/25"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 border border-slate-700"
              }`}
              data-testid="location-tab-all"
            >
              All Locations
            </button>
            {(localClient.locations || []).map(loc => {
              const summary = locationSummary.find(l => l.name === loc);
              return (
                <button
                  key={loc}
                  onClick={() => setActiveLocation(loc)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeLocation === loc
                      ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/25"
                      : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 border border-slate-700"
                  }`}
                  data-testid={`location-tab-${loc}`}
                >
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {loc}
                  {summary?.impressions > 0 && (
                    <span className="ml-1.5 text-[11px] opacity-70">
                      ({summary.impressions >= 1000 ? Math.round(summary.impressions / 1000) + 'K' : summary.impressions})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {activeLocation !== "all" && (
            <p className="text-slate-400 text-xs mt-2">
              Showing data for <span className="text-white font-medium">{activeLocation}</span> location only. GA4 Analytics shows company-wide data.
            </p>
          )}
        </div>
      )}

      {/* STICKY CHAPTER NAV — appears once cover scrolls out of view */}
      {/* StickyChapterNav removed per user request (Apr 20, 2026) */}

      {/* REPORT AT A GLANCE — shown on every tab (single + multi, every location) */}
      {/* BRAND MANIFESTO — sets the frame before numbers */}
      <BrandManifesto
        clientName={localClient.name}
        preparedDate={readOnly ? new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null}
      />

      <ReportAtAGlance
        periodControl={periodControl}
        clientId={localClient.id}
        API={API}
        clientName={localClient.name}
        reportStart={localClient.report_start}
        reportEnd={localClient.report_end}
        closeRatePct={localClient.close_rate_pct ?? 2.0}
        onImportData={onViewChange ? () => onViewChange("import") : undefined}
        mobileSummary={mobileSummary}
        metaSummary={metaSummary}
        socialMirrorSummary={socialMirrorSummary}
        facebookCampaign={facebookCampaign}
        ctvPublishers={ctvPublishers}
        googleAdsData={googleAdsData}
        tiktokImpressions={tiktokTotals.impressions}
        tiktokClicks={tiktokTotals.clicks}
        tiktokReach={tiktokTotals.reach}
        pmaxImpressions={pmaxSum.impressions}
        pmaxClicks={pmaxSum.clicks}
        appfrontData={appfrontData}
        brandChannelImpressions={brandChannelImpressions}
        trafficData={trafficData}
        analyticsMonthly={analyticsMonthly}
        beforeAfter={beforeAfter}
        liveChatRecords={liveChatRecords}
        visitorIdRecords={visitorIdRecords}
        agencyNote={agencyNote}
        onSaveAgencyNote={saveAgencyNote}
        readOnly={readOnly}
        previousPeriod={previousPeriod}
      />

      {/* CHAPTER 01 (DROPDOWN): THE PAYOFF — ROI Story */}
      {activeLocation === 'all' && (
        <ChapterDropdown
          id="chapter-payoff"
          number="01"
          kicker="Start here"
          title="The Payoff"
          prompt={`Did advertising work for ${localClient.name}? Here's the answer, first. Then the proof.`}
          accent="emerald"
          sectionCount="ROI Story"
        >
          <ROIStoryHero
            clientName={localClient.name}
            reportStart={localClient.report_start}
            reportEnd={localClient.report_end}
            mobileSummary={mobileSummary}
            metaSummary={metaSummary}
            socialMirrorSummary={socialMirrorSummary}
            facebookCampaign={facebookCampaign}
            ctvPublishers={ctvPublishers}
            googleAdsData={googleAdsData}
            tiktokImpressions={tiktokTotals.impressions}
            tiktokClicks={tiktokTotals.clicks}
            tiktokReach={tiktokTotals.reach}
            pmaxImpressions={pmaxSum.impressions}
            pmaxClicks={pmaxSum.clicks}
            previousPeriod={previousPeriod}
            payoffNote={payoffNote}
            onSavePayoffNote={savePayoffNote}
            readOnly={readOnly}
            appfrontData={appfrontData}
            brandChannelImpressions={brandChannelImpressions}
            trafficData={trafficData}
            analyticsData={analyticsData}
          />
        </ChapterDropdown>
      )}

      {/* LANDING PAGE MOVED TO CHAPTER 04 (parking spot) — see below */}

          {/* CHAPTER 02 (DROPDOWN): EVERY CHANNEL, UP CLOSE — all channel sections inside */}
          <ChapterDropdown
            id="chapter-channels"
            number="02"
            kicker="Proof, channel by channel"
            title="Every Channel, Up Close"
            prompt="Each channel's story in a sentence. Tap See the numbers on any channel for the full detail."
            accent="violet"
          >

          <CampaignSummaryDaily API={API} clientId={localClient.id} reportStart={localClient.report_start} reportEnd={localClient.report_end} />

          {/* META/FACEBOOK CAMPAIGN PERFORMANCE */}
          {(metaSummary && (metaSummary.placements?.length > 0 || metaSummary.top_creatives?.length > 0)) || filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta').length > 0 || (localClient.client_type !== "multi" && facebookCampaign?.has_data) ? (
            <CollapsibleCard
              testId="meta-facebook-section"
              story={channelStories.meta}
              containerClass="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-2xl p-4 md:p-6 border border-blue-500/20"
              chevronClass="text-blue-300"
              header={
                <SectionReportHeader
                  clientName={localClient.name}
                  platformLabel="Facebook"
                  reportTitle="Facebook & Instagram Ads"
                  reportStart={localClient.report_start}
                  reportEnd={localClient.report_end}
                  accentColor="#60a5fa"
                  iconBgClass="bg-blue-600"
                  icon={<svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>}
                />
              }
            >
                {localClient.client_type !== "multi" ? (
                  <MetaStoryLayout
                    clientName={localClient.name}
                    metaSummary={metaSummary}
                    deviceMetrics={deviceMetrics}
                    facebookCampaign={facebookCampaign}
                    fbAds={filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta')}
                    CreativeIcon={CreativeIcon}
                    clientId={localClient.id}
                    API={API}
                    readOnly={readOnly}
                    reportStart={localClient.report_start}
                    reportEnd={localClient.report_end}
                  />
                ) : (<>

                {/* Meta Summary Content (from PDF imports) */}
                {metaSummary && (metaSummary.placements?.length > 0 || metaSummary.top_creatives?.length > 0) && (<>
                {/* Devices & Performance Overview — Combined */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
                  {(() => {
                    // Aggregate device metrics
                    const devTotals = {};
                    deviceMetrics.filter(d => d.platform === 'facebook' || d.platform === 'meta').forEach(d => {
                      const dev = d.device || 'Unknown';
                      if (!devTotals[dev]) devTotals[dev] = { impressions: 0, clicks: 0 };
                      devTotals[dev].impressions += d.impressions || 0;
                      devTotals[dev].clicks += d.clicks || 0;
                    });
                    const totalDevImp = Object.values(devTotals).reduce((s, v) => s + v.impressions, 0);
                    const totalDevClicks = Object.values(devTotals).reduce((s, v) => s + v.clicks, 0);

                    const COLORS = { Mobile: '#10b981', Tablet: '#f59e0b', Desktop: '#3b82f6' };
                    const deviceOrder = ['Mobile', 'Tablet', 'Desktop'].filter(d => devTotals[d]?.impressions > 0);

                    // Donut math
                    const radius = 50;
                    const circumference = 2 * Math.PI * radius;
                    let offset = 0;

                    // Ad-level stats
                    const fbAds = filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta');
                    const totalReach = fbAds.reduce((s, a) => s + (a.reach || 0), 0);
                    const totalLPV = fbAds.reduce((s, a) => s + (a.landing_page_views || 0), 0);

                    // Fall back to meta summary impressions when no device-level
                    // breakdown is available (most clients only import the
                    // campaign performance PDF, not the device split).
                    const summaryImp = metaSummary?.overview?.total_impressions || 0;
                    const hasDeviceData = totalDevImp > 0 && deviceOrder.length > 0;
                    const headlineImp = hasDeviceData ? totalDevImp : summaryImp;

                    return (
                      <div className="flex flex-col lg:flex-row items-center gap-6">
                        {/* Impressions visual: donut when device data exists,
                            otherwise a clean "Total Impressions" tile so the
                            number is never lost. */}
                        {hasDeviceData ? (
                          <div className="relative w-44 h-44 flex-shrink-0">
                            <svg className="w-44 h-44 -rotate-90" viewBox="0 0 120 120">
                              <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="14" />
                              {deviceOrder.map(dev => {
                                const pct = totalDevImp > 0 ? (devTotals[dev].impressions / totalDevImp) : 0;
                                const dash = pct * circumference;
                                const el = (
                                  <circle key={dev} cx="60" cy="60" r={radius} fill="none"
                                    stroke={COLORS[dev]} strokeWidth="14" strokeLinecap="round"
                                    strokeDasharray={`${dash} ${circumference}`}
                                    strokeDashoffset={`${-offset}`}
                                  />
                                );
                                offset += dash;
                                return el;
                              })}
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-white font-bold text-lg">{headlineImp.toLocaleString()}</span>
                              <span className="text-slate-400 text-[11px]">Impressions</span>
                            </div>
                          </div>
                        ) : (
                          <div className="w-44 h-44 flex-shrink-0 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex flex-col items-center justify-center" data-testid="meta-impressions-tile">
                            <span className="text-blue-300 text-[10px] font-bold uppercase tracking-wider mb-1">Total Impressions</span>
                            <span className="text-white font-black text-3xl tabular-nums">{headlineImp.toLocaleString()}</span>
                            <span className="text-slate-400 text-[11px] mt-1">across all placements</span>
                          </div>
                        )}

                        {/* Right side: Device breakdown + key stats */}
                        <div className="flex-1 w-full">
                          {hasDeviceData ? (<>
                          <p className="text-white font-semibold text-sm mb-3">Devices Where Your Ads Appeared</p>
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            {deviceOrder.map(dev => {
                              const data = devTotals[dev];
                              const pct = totalDevImp > 0 ? ((data.impressions / totalDevImp) * 100).toFixed(1) : 0;
                              return (
                                <div key={dev} className="bg-white/5 rounded-lg p-3 border border-white/5">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[dev] }} />
                                    <span className="text-white font-medium text-sm">{dev}</span>
                                  </div>
                                  <p className="text-white font-bold text-lg">{pct}%</p>
                                  <p className="text-slate-500 text-[11px]">{data.impressions.toLocaleString()} impressions</p>
                                  {data.clicks > 0 && <p className="text-indigo-400 text-[11px]">{data.clicks.toLocaleString()} clicks</p>}
                                </div>
                              );
                            })}
                          </div>
                          </>) : (
                            <p className="text-white font-semibold text-sm mb-3">Performance at a Glance</p>
                          )}

                          {/* Key stats row */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                            <div className="bg-emerald-500/10 rounded-xl p-4 text-center border border-emerald-500/20">
                              <p className="text-emerald-400 font-bold text-2xl">{metaSummary.overview.total_clicks?.toLocaleString()}</p>
                              <p className="text-slate-400 text-xs mt-1">Total Clicks</p>
                            </div>
                            <div className="bg-amber-500/10 rounded-xl p-4 text-center border border-amber-500/20">
                              <p className="text-amber-400 font-bold text-2xl">{metaSummary.overview.ctr}%</p>
                              <p className="text-slate-400 text-xs mt-1">Click-Through Rate</p>
                            </div>
                            {totalReach > 0 && (
                              <div className="bg-violet-500/10 rounded-xl p-4 text-center border border-violet-500/20">
                                <p className="text-violet-400 font-bold text-2xl">{totalReach >= 1000 ? Math.round(totalReach / 1000) + 'K' : totalReach.toLocaleString()}</p>
                                <p className="text-slate-400 text-xs mt-1">People Reached</p>
                              </div>
                            )}
                            {totalLPV > 0 && (
                              <div className="bg-pink-500/10 rounded-xl p-4 text-center border border-pink-500/20">
                                <p className="text-pink-400 font-bold text-2xl">{totalLPV >= 1000 ? (totalLPV / 1000).toFixed(1) + 'K' : totalLPV.toLocaleString()}</p>
                                <p className="text-slate-400 text-xs mt-1">Landing Page Views</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* CTR Benchmark - Meta */}
                {metaSummary.overview.ctr && parseFloat(metaSummary.overview.ctr) > 0 && (() => {
                  const benchmark = 0.90;
                  // Find the top 3 creatives driving clicks (aggregate by name)
                  const creativesMap = {};
                  if (facebookCampaign?.has_data && facebookCampaign?.campaigns?.length > 0) {
                    facebookCampaign.campaigns.forEach(c => {
                      (c.creatives || []).forEach(cr => {
                        const key = cr.name || 'Unknown';
                        if (!creativesMap[key]) creativesMap[key] = { name: key, impressions: 0, clicks: 0 };
                        creativesMap[key].impressions += cr.impressions || 0;
                        creativesMap[key].clicks += cr.clicks || 0;
                      });
                    });
                  }
                  const top3 = Object.values(creativesMap)
                    .filter(c => c.impressions > 100 && c.clicks > 0)
                    .map(c => ({ ...c, ctr: Number(((c.clicks / c.impressions) * 100).toFixed(2)) }))
                    .sort((a, b) => (b.impressions * b.ctr) - (a.impressions * a.ctr))
                    .slice(0, 3);

                  const hasTop3 = top3.length > 0;
                  const combinedImp = top3.reduce((s, c) => s + (c.impressions || 0), 0);
                  const combinedClicks = top3.reduce((s, c) => s + (c.clicks || 0), 0);
                  const combinedCtr = combinedImp > 0 ? Number(((combinedClicks / combinedImp) * 100).toFixed(2)) : parseFloat(metaSummary.overview.ctr);
                  const isAbove = combinedCtr > benchmark;
                  const multiplier = (combinedCtr / benchmark).toFixed(1);

                  const gaugeR = 52;
                  const gaugeCirc = 2 * Math.PI * gaugeR;
                  const gaugeHalf = gaugeCirc / 2;
                  const fillPct = Math.min(Number(multiplier) / 5, 1);
                  const gaugeFill = fillPct * gaugeHalf;

                  return (
                  <div className="flex gap-4 mb-4">
                    {/* Left box — text + creatives */}
                    <div className={`flex-1 min-w-0 bg-gradient-to-r ${isAbove ? 'from-emerald-500/10 to-teal-500/10 border-emerald-500/25' : 'from-amber-500/10 to-orange-500/10 border-amber-500/25'} rounded-xl p-5 border`}>
                      <p className="text-white font-semibold text-sm">{isAbove ? '🏆 Your Top Creatives Are Crushing It!' : '📊 Your Paid Social Performance'}</p>
                      <p className="text-slate-300 text-xs mt-2 leading-relaxed">
                        The cross-industry average CTR for paid Facebook & Instagram ads is <span className="text-slate-100 font-semibold">{benchmark}%</span>.
                        {hasTop3 ? (
                          <> Your top {top3.length} creatives, representing <span className="text-white font-semibold">{combinedImp.toLocaleString()} impressions</span>, are driving a combined <span className={`${isAbove ? 'text-emerald-400' : 'text-amber-400'} font-bold`}>{combinedCtr}% CTR</span>, which is <span className={`${isAbove ? 'text-emerald-400' : 'text-amber-400'} font-bold`}>{multiplier}x</span> the industry average.</>
                        ) : (
                          <> Your campaigns are performing at <span className={`${isAbove ? 'text-emerald-400' : 'text-amber-400'} font-bold`}>{multiplier}x</span> that benchmark with a <span className={`${isAbove ? 'text-emerald-400' : 'text-amber-400'} font-bold`}>{metaSummary.overview.ctr}% CTR</span>.</>
                        )}
                      </p>
                      {hasTop3 && (
                        <div className="mt-3 space-y-1.5">
                          {top3.map((c, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className={`font-bold w-4 flex-shrink-0 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-slate-300' : 'text-amber-600'}`}>#{i + 1}</span>
                              <CreativeIcon campaignType="facebook" adName={c.name} />
                              <span className="text-slate-400 truncate flex-1">{c.name}</span>
                              <span className="text-slate-500 text-[11px] flex-shrink-0">{(c.impressions || 0).toLocaleString()} imp</span>
                              <span className={`font-semibold flex-shrink-0 ${c.ctr > benchmark ? 'text-emerald-400' : 'text-amber-400'}`}>{c.ctr}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-slate-400 text-[11px] mt-3 leading-relaxed">
                        {isAbove ? (
                          <>Creative is resonating and targeting is dialed in: people are stopping and clicking.</>
                        ) : (
                          <>Paid social builds brand awareness that compounds: repeated exposure increases recall and trust.</>
                        )}
                      </p>
                    </div>
                    {/* Right box — radial gauge */}
                    <div className={`flex-shrink-0 w-44 ${isAbove ? 'bg-emerald-500/5 border-emerald-500/25' : 'bg-amber-500/5 border-amber-500/25'} rounded-xl border flex flex-col items-center justify-center p-4`}>
                      <p className="text-slate-400 text-[11px] font-medium mb-2">vs Industry Benchmark</p>
                      <div className="relative w-32 h-20">
                        <svg className="w-32 h-20" viewBox="0 0 130 75">
                          <path d="M 10 70 A 52 52 0 0 1 120 70" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" strokeLinecap="round" />
                          <path d="M 10 70 A 52 52 0 0 1 120 70" fill="none"
                            stroke={isAbove ? '#10b981' : '#f59e0b'} strokeWidth="10" strokeLinecap="round"
                            strokeDasharray={`${gaugeFill} ${gaugeHalf}`} />
                          <line x1="65" y1="70" x2={65 + 38 * Math.cos(Math.PI + fillPct * Math.PI)} y2={70 + 38 * Math.sin(Math.PI + fillPct * Math.PI)}
                            stroke="white" strokeWidth="2" strokeLinecap="round" />
                          <circle cx="65" cy="70" r="3" fill="white" />
                          <text x="10" y="65" fill="#64748b" fontSize="7" textAnchor="middle">0x</text>
                          <text x="120" y="65" fill="#64748b" fontSize="7" textAnchor="middle">5x</text>
                        </svg>
                      </div>
                      <span className={`font-bold text-2xl ${isAbove ? 'text-emerald-400' : 'text-amber-400'}`}>{multiplier}x</span>
                      <p className="text-slate-500 text-[9px] mt-0.5">Avg CTR: {benchmark}%</p>
                    </div>
                  </div>
                  );
                })()}

                {/* How Your Ads Reached People — Aggregated across all campaigns */}
                {facebookCampaign?.has_data && facebookCampaign?.campaigns?.length > 0 && (() => {
                  const allLineItems = facebookCampaign.campaigns.flatMap(c => c.line_items || []);
                  if (allLineItems.length === 0) return null;
                  const retargeting = { impressions: 0, clicks: 0 };
                  const interest = { impressions: 0, clicks: 0 };
                  allLineItems.forEach(item => {
                    const name = (item.name || '').toLowerCase();
                    const bucket = (name.includes('retargeting') || name.includes('retarget')) ? retargeting : interest;
                    bucket.impressions += item.impressions || 0;
                    bucket.clicks += item.clicks || 0;
                  });
                  const totalImp = retargeting.impressions + interest.impressions;
                  if (totalImp === 0) return null;
                  const retargetPct = ((retargeting.impressions / totalImp) * 100).toFixed(1);
                  const interestPct = ((interest.impressions / totalImp) * 100).toFixed(1);
                  const retargetCtr = retargeting.impressions > 0 ? ((retargeting.clicks / retargeting.impressions) * 100).toFixed(2) : 0;
                  const interestCtr = interest.impressions > 0 ? ((interest.clicks / interest.impressions) * 100).toFixed(2) : 0;
                  const betterStrategy = Number(retargetCtr) > Number(interestCtr) ? 'retargeting' : 'interest';
                  const radius = 50;
                  const circumference = 2 * Math.PI * radius;
                  const retargetDash = (Number(retargetPct) / 100) * circumference;
                  const interestDash = (Number(interestPct) / 100) * circumference;

                  return (
                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
                      <p className="text-white font-semibold mb-4">How Your Ads Reached People</p>
                      <div className="flex flex-col lg:flex-row items-center gap-6 mb-5">
                        <div className="relative w-44 h-44 flex-shrink-0">
                          <svg className="w-44 h-44 -rotate-90" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="14" />
                            {retargeting.impressions > 0 && (
                              <circle cx="60" cy="60" r={radius} fill="none" stroke="#8b5cf6" strokeWidth="14" strokeLinecap="round"
                                strokeDasharray={`${retargetDash} ${circumference}`} />
                            )}
                            {interest.impressions > 0 && (
                              <circle cx="60" cy="60" r={radius} fill="none" stroke="#f59e0b" strokeWidth="14" strokeLinecap="round"
                                strokeDasharray={`${interestDash} ${circumference}`}
                                strokeDashoffset={`${-retargetDash}`} />
                            )}
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-white font-bold text-lg">{totalImp.toLocaleString()}</span>
                            <span className="text-slate-400 text-[11px]">Total Impressions</span>
                          </div>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                          {retargeting.impressions > 0 && (
                            <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/25">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full bg-violet-500" />
                                <p className="text-violet-400 font-semibold text-sm">Retargeting</p>
                              </div>
                              <p className="text-xs text-slate-400 mb-3">People who already visited your site or engaged with your content</p>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div><p className="text-white font-bold text-sm">{retargetPct}%</p><p className="text-slate-500 text-[11px]">of Impressions</p></div>
                                <div><p className="text-white font-bold text-sm">{retargeting.clicks.toLocaleString()}</p><p className="text-slate-500 text-[11px]">Clicks</p></div>
                                <div><p className={`font-bold text-sm ${betterStrategy === 'retargeting' ? 'text-emerald-400' : 'text-white'}`}>{retargetCtr}%</p><p className="text-slate-500 text-[11px]">CTR</p></div>
                              </div>
                            </div>
                          )}
                          {interest.impressions > 0 && (
                            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/25">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full bg-amber-500" />
                                <p className="text-amber-400 font-semibold text-sm">Interest-Based</p>
                              </div>
                              <p className="text-xs text-slate-400 mb-3">People "in the market" based on their behavior and interests</p>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div><p className="text-white font-bold text-sm">{interestPct}%</p><p className="text-slate-500 text-[11px]">of Impressions</p></div>
                                <div><p className="text-white font-bold text-sm">{interest.clicks.toLocaleString()}</p><p className="text-slate-500 text-[11px]">Clicks</p></div>
                                <div><p className={`font-bold text-sm ${betterStrategy === 'interest' ? 'text-emerald-400' : 'text-white'}`}>{interestCtr}%</p><p className="text-slate-500 text-[11px]">CTR</p></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-violet-500/10 to-amber-500/10 rounded-lg p-4 border border-violet-500/15">
                        <p className="text-slate-300 text-sm leading-relaxed">
                          {retargeting.impressions > 0 && interest.impressions > 0 ? (
                            <>
                              <span className="text-violet-400 font-semibold">{retargetPct}%</span> of your impressions went to <span className="text-white font-medium">retargeting</span>, people who already know your brand, while{' '}
                              <span className="text-amber-400 font-semibold">{interestPct}%</span> went to <span className="text-white font-medium">interest-based audiences</span>: new people "in the market" based on their behavior.
                              {betterStrategy === 'retargeting' ? (
                                <> Retargeting is outperforming at <span className="text-emerald-400 font-semibold">{retargetCtr}% CTR</span> vs {interestCtr}%, which makes sense: people who've already interacted with your brand are more likely to click again.</>
                              ) : (
                                <> Interest-based targeting is performing strongly at <span className="text-emerald-400 font-semibold">{interestCtr}% CTR</span> vs {retargetCtr}% for retargeting, showing your content resonates well with new audiences.</>
                              )}
                            </>
                          ) : (
                            <>Your campaigns delivered <span className="text-white font-semibold">{totalImp.toLocaleString()} impressions</span> across {allLineItems.length} targeting strategies.</>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Ad Creative & Performance */}
                {(() => {
                  const rawCreatives = [];
                  if (facebookCampaign?.has_data && facebookCampaign?.campaigns?.length > 0) {
                    facebookCampaign.campaigns.forEach(c => {
                      (c.creatives || []).forEach(cr => rawCreatives.push({ ...cr, source: c.campaign_name }));
                    });
                  }
                  // Fallback to ad_metrics if no facebookCampaign creatives
                  if (rawCreatives.length === 0) {
                    filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta').forEach(ad => {
                      rawCreatives.push({ name: ad.ad_name, impressions: ad.impressions, clicks: ad.link_clicks || ad.clicks || 0, ctr: ad.ctr || 0 });
                    });
                  }
                  if (rawCreatives.length === 0) return null;
                  // Aggregate by name to merge duplicates across date ranges
                  const aggMap = {};
                  rawCreatives.forEach(cr => {
                    const key = cr.name || 'Unknown';
                    if (!aggMap[key]) aggMap[key] = { name: key, impressions: 0, clicks: 0, source: cr.source };
                    aggMap[key].impressions += cr.impressions || 0;
                    aggMap[key].clicks += cr.clicks || 0;
                  });
                  const allCreatives = Object.values(aggMap);
                  const scored = allCreatives
                    .filter(c => (c.impressions || 0) > 0)
                    .map(c => ({
                      ...c,
                      ctr: c.clicks > 0 && c.impressions > 0 ? Number(((c.clicks / c.impressions) * 100).toFixed(2)) : 0,
                      score: (c.impressions || 0) * (c.clicks > 0 && c.impressions > 0 ? (c.clicks / c.impressions * 100) : 0),
                    }))
                    .sort((a, b) => b.score - a.score);

                  const topPerformers = scored.slice(0, 3);
                  const maxImp = Math.max(...scored.map(c => c.impressions), 1);

                  return (
                    <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
                      {/* Top Performers */}
                      {topPerformers.length > 0 && (
                        <>
                          <p className="text-white font-semibold mb-4">Top Performing Ads</p>
                          <p className="text-slate-400 text-xs mb-4">Ranked by highest combination of reach and engagement</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                            {topPerformers.map((ad, idx) => {
                              const impBar = (ad.impressions / maxImp) * 100;
                              const medals = ['from-amber-400 to-yellow-500', 'from-slate-300 to-slate-400', 'from-amber-700 to-amber-600'];
                              const medalLabels = ['#1', '#2', '#3'];
                              return (
                                <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10 relative overflow-hidden">
                                  <div className={`absolute top-0 right-0 w-10 h-10 bg-gradient-to-br ${medals[idx] || medals[2]} rounded-bl-xl flex items-center justify-center`}>
                                    <span className="text-xs font-black text-slate-900">{medalLabels[idx]}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-3 pr-10">
                                    <CreativeIcon campaignType="facebook" adName={ad.name} />
                                    <p className="text-white font-medium text-sm truncate">{ad.name}</p>
                                  </div>
                                  <div className="relative h-2 bg-white/5 rounded-full mb-3 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" style={{ width: `${Math.max(impBar, 5)}%` }} />
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                      <p className="text-blue-400 font-bold text-sm">{ad.impressions?.toLocaleString()}</p>
                                      <p className="text-slate-500 text-[11px]">Impressions</p>
                                    </div>
                                    <div>
                                      <p className="text-indigo-400 font-bold text-sm">{(ad.clicks || 0).toLocaleString()}</p>
                                      <p className="text-slate-500 text-[11px]">Clicks</p>
                                    </div>
                                    <div>
                                      <p className="text-emerald-400 font-bold text-sm">{ad.ctr}%</p>
                                      <p className="text-slate-500 text-[11px]">CTR</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}

                      {/* Grouped by Creative — separate cards */}
                      {(() => {
                        // Group creatives by core file name
                        const grouped = {};
                        scored.forEach(ad => {
                          let core = ad.name;
                          // Strip location prefix (e.g., "Sticks CVille_")
                          core = core.replace(/^[A-Za-z\s]+[_\s]+/, '');
                          // Strip date/version prefix (e.g., "1.5_", "2.20_", "12.2_")
                          core = core.replace(/^\d+\.\d+[_\s]*/, '');
                          // Strip " - Copy N" suffixes
                          core = core.replace(/\s*-\s*Copy\s*\d*/i, '');
                          // Strip dimension suffixes for grouping (1080x1080, 1080x1350)
                          const coreNoSize = core.replace(/\d{3,4}x\d{3,4}/g, '').replace(/[_\s]+/g, ' ').trim();
                          // Use the no-size version if it still has substance, otherwise keep original
                          const groupKey = coreNoSize.replace(/\(\d+\)/g, '').replace(/\s+/g, ' ').trim() || core;
                          if (!grouped[groupKey]) {
                            grouped[groupKey] = { name: groupKey, displayName: core, impressions: 0, clicks: 0, entries: [] };
                          }
                          grouped[groupKey].impressions += ad.impressions || 0;
                          grouped[groupKey].clicks += ad.clicks || 0;
                          grouped[groupKey].entries.push(ad);
                        });
                        const groups = Object.values(grouped)
                          .map(g => ({
                            ...g,
                            ctr: g.impressions > 0 ? Number(((g.clicks / g.impressions) * 100).toFixed(2)) : 0,
                          }))
                          .sort((a, b) => b.ctr - a.ctr);

                        // Exclude creatives already shown in Top Performing Ads
                        const topNames = new Set(topPerformers.map(tp => {
                          let core = tp.name;
                          core = core.replace(/^[A-Za-z\s]+[_\s]+/, '');
                          core = core.replace(/^\d+\.\d+[_\s]*/, '');
                          core = core.replace(/\s*-\s*Copy\s*\d*/i, '');
                          const coreNoSize = core.replace(/\d{3,4}x\d{3,4}/g, '').replace(/[_\s]+/g, ' ').trim();
                          return coreNoSize.replace(/\(\d+\)/g, '').replace(/\s+/g, ' ').trim() || core;
                        }));
                        const filteredGroups = groups.filter(g => !topNames.has(g.name));

                        return (
                          <div className="rounded-xl border border-white/10 overflow-hidden">
                            <div
                              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                              onClick={() => setExpandedCampaigns(prev => ({ ...prev, meta_all_creatives: !prev.meta_all_creatives }))}
                              data-testid="meta-all-creatives-toggle"
                            >
                              <div className="flex items-center gap-2">
                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedCampaigns.meta_all_creatives ? '' : '-rotate-90'}`} />
                                <p className="text-white font-semibold text-sm">Click to View: Ad Creative Performance</p>
                              </div>
                              <span className="text-xs text-slate-500">{filteredGroups.length} creatives</span>
                            </div>
                            {expandedCampaigns.meta_all_creatives && (
                              <div className="px-4 pb-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
                                {filteredGroups.map((group, gIdx) => (
                                  <div key={gIdx} className="bg-white/5 rounded-lg p-3 border border-white/5">
                                    <div className="flex items-center gap-1.5 mb-2">
                                      <CreativeIcon campaignType="facebook" adName={group.name} />
                                      <p className="text-white font-medium text-xs flex-1 truncate">{group.name}</p>
                                      {gIdx === 0 && <span className="text-[8px] font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full">TOP</span>}
                                    </div>
                                    <div className="flex items-center justify-between text-center">
                                      <div>
                                        <p className="text-blue-400 font-bold text-sm">{group.impressions >= 1000 ? Math.round(group.impressions / 1000) + 'K' : group.impressions.toLocaleString()}</p>
                                        <p className="text-slate-500 text-[9px]">Imp</p>
                                      </div>
                                      <div>
                                        <p className="text-indigo-400 font-bold text-sm">{group.clicks.toLocaleString()}</p>
                                        <p className="text-slate-500 text-[9px]">Clicks</p>
                                      </div>
                                      <div>
                                        <p className={`font-bold text-sm ${group.ctr >= 2 ? 'text-emerald-400' : group.ctr >= 1 ? 'text-amber-400' : 'text-slate-400'}`}>{group.ctr}%</p>
                                        <p className="text-slate-500 text-[9px]">CTR</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    </div>
                  );
                })()}

                {/* Where Ads Appeared - Pie Chart */}
                {metaSummary.placements && metaSummary.placements.length > 0 && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <p className="text-white font-semibold mb-4">Where Your Ads Appeared</p>
                  
                  {(() => {
                    const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4'];
                    const placementIcons = {
                      'Facebook Feed': '📰',
                      'Facebook Reels': '🎬',
                      'Facebook In-stream Videos': '📺',
                      'Instagram Stories': '📸',
                      'Facebook Stories': '📱',
                      'Facebook Marketplace': '🛒',
                      'Instagram Feed': '📷',
                      'Instagram Reels': '🎥',
                      'Audience Network': '🌐'
                    };
                    
                    const pieData = metaSummary.placements.slice(0, 6).map((p, idx) => ({
                      name: p.placement,
                      value: p.impressions,
                      clicks: p.clicks,
                      ctr: p.ctr,
                      fill: COLORS[idx % COLORS.length],
                      icon: placementIcons[p.placement] || '📊'
                    }));
                    
                    const totalImpressions = pieData.reduce((sum, p) => sum + p.value, 0);
                    
                    return (
                      <div className="flex flex-col lg:flex-row items-center gap-6">
                        {/* Pie Chart */}
                        <div className="w-64 h-64 flex-shrink-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={90}
                                paddingAngle={2}
                                dataKey="value"
                                stroke="none"
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#1e293b', 
                                  border: '1px solid #334155',
                                  borderRadius: '8px',
                                  padding: '8px 12px'
                                }}
                                formatter={(value, name) => [`${value.toLocaleString()} impressions`, name]}
                                labelStyle={{ color: '#94a3b8', fontWeight: 'bold', fontSize: '12px' }}
                                itemStyle={{ color: '#e2e8f0', fontSize: '13px' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        
                        {/* Legend */}
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {pieData.map((placement, idx) => {
                            const percentage = ((placement.value / totalImpressions) * 100).toFixed(1);
                            return (
                              <div 
                                key={idx} 
                                className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/10"
                              >
                                <div 
                                  className="w-4 h-4 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: placement.fill }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{placement.icon}</span>
                                    <span className="text-white text-sm font-medium truncate">{placement.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="text-slate-400">{percentage}% Impressions</span>
                                    {placement.clicks > 0 && (
                                      <>
                                        <span className="text-slate-600">·</span>
                                        <span className="text-blue-400">{(placement.clicks / Math.max(placement.value, 1) * 100).toFixed(2)}% CTR</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                )}

                {/* Gender Performance Insight - Visual Comparison */}
                {metaSummary.gender && metaSummary.gender.length > 0 && (
                  <div className="mt-6 bg-gradient-to-r from-pink-500/10 to-blue-500/10 rounded-xl p-5 border border-pink-500/20">
                    <p className="text-white font-semibold mb-4">Audience Engagement by Gender</p>
                    
                    {(() => {
                      const female = metaSummary.gender.find(g => g.segment === 'Female') || { impressions: 0, clicks: 0, ctr: 0 };
                      const male = metaSummary.gender.find(g => g.segment === 'Male') || { impressions: 0, clicks: 0, ctr: 0 };
                      const totalImpressions = female.impressions + male.impressions;
                      const femalePercent = totalImpressions > 0 ? (female.impressions / totalImpressions * 100).toFixed(1) : 0;
                      const malePercent = totalImpressions > 0 ? (male.impressions / totalImpressions * 100).toFixed(1) : 0;
                      const higherCTR = female.ctr > male.ctr ? 'female' : male.ctr > female.ctr ? 'male' : 'equal';
                      
                      return (
                        <div className="space-y-4">
                          {/* Visual Split Bar */}
                          <div className="relative h-12 rounded-full overflow-hidden bg-slate-800 flex">
                            <div 
                              className="h-full bg-gradient-to-r from-pink-500 to-pink-400 flex items-center justify-start pl-4 transition-all duration-500"
                              style={{ width: `${femalePercent}%` }}
                            >
                              {parseFloat(femalePercent) > 15 && (
                                <span className="text-white font-bold text-sm">{femalePercent}%</span>
                              )}
                            </div>
                            <div 
                              className="h-full bg-gradient-to-r from-blue-400 to-blue-500 flex items-center justify-end pr-4 transition-all duration-500"
                              style={{ width: `${malePercent}%` }}
                            >
                              {parseFloat(malePercent) > 15 && (
                                <span className="text-white font-bold text-sm">{malePercent}%</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Gender Cards */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Female */}
                            <div className={`bg-pink-500/10 rounded-xl p-4 border ${higherCTR === 'female' ? 'border-pink-400 ring-2 ring-pink-400/30' : 'border-pink-500/30'}`}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
                                  <span className="text-2xl">👩</span>
                                </div>
                                <div>
                                  <p className="text-white font-semibold">Women</p>
                                  <p className="text-pink-400 text-sm font-medium">{femalePercent}% of audience</p>
                                </div>
                                {higherCTR === 'female' && (
                                  <span className="ml-auto px-2 py-1 bg-pink-500/30 rounded-full text-pink-300 text-xs font-medium">
                                    Higher CTR
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <p className="text-white font-bold">
                                    {female.impressions >= 1000 ? Math.round(female.impressions / 1000) + 'K' : female.impressions.toLocaleString()}
                                  </p>
                                  <p className="text-slate-500 text-xs">Impressions</p>
                                </div>
                                <div>
                                  <p className="text-white font-bold">{female.clicks?.toLocaleString() || 0}</p>
                                  <p className="text-slate-500 text-xs">Clicks</p>
                                </div>
                                <div>
                                  <p className={`font-bold ${higherCTR === 'female' ? 'text-pink-400' : 'text-white'}`}>{female.ctr}%</p>
                                  <p className="text-slate-500 text-xs">CTR</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Male */}
                            <div className={`bg-blue-500/10 rounded-xl p-4 border ${higherCTR === 'male' ? 'border-blue-400 ring-2 ring-blue-400/30' : 'border-blue-500/30'}`}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <span className="text-2xl">👨</span>
                                </div>
                                <div>
                                  <p className="text-white font-semibold">Men</p>
                                  <p className="text-blue-400 text-sm font-medium">{malePercent}% of audience</p>
                                </div>
                                {higherCTR === 'male' && (
                                  <span className="ml-auto px-2 py-1 bg-blue-500/30 rounded-full text-blue-300 text-xs font-medium">
                                    Higher CTR
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                  <p className="text-white font-bold">
                                    {male.impressions >= 1000 ? Math.round(male.impressions / 1000) + 'K' : male.impressions.toLocaleString()}
                                  </p>
                                  <p className="text-slate-500 text-xs">Impressions</p>
                                </div>
                                <div>
                                  <p className="text-white font-bold">{male.clicks?.toLocaleString() || 0}</p>
                                  <p className="text-slate-500 text-xs">Clicks</p>
                                </div>
                                <div>
                                  <p className={`font-bold ${higherCTR === 'male' ? 'text-blue-400' : 'text-white'}`}>{male.ctr}%</p>
                                  <p className="text-slate-500 text-xs">CTR</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Insight callout */}
                          {higherCTR !== 'equal' && (
                            <div className={`flex items-center gap-2 p-3 rounded-lg ${higherCTR === 'female' ? 'bg-pink-500/10' : 'bg-blue-500/10'}`}>
                              <span className="text-lg">💡</span>
                              <p className={`text-sm ${higherCTR === 'female' ? 'text-pink-300' : 'text-blue-300'}`}>
                                <span className="font-semibold">{higherCTR === 'female' ? 'Women' : 'Men'}</span> are engaging more with your ads 
                                ({higherCTR === 'female' ? female.ctr : male.ctr}% vs {higherCTR === 'female' ? male.ctr : female.ctr}% CTR)
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Campaign Strategy Breakdown */}
                {metaSummary.campaigns && metaSummary.campaigns.length > 0 && (
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {metaSummary.campaigns.map((campaign, idx) => (
                      <div 
                        key={idx} 
                        className={`rounded-xl p-4 border ${campaign.strategy === 'Retargeting' 
                          ? 'bg-amber-500/10 border-amber-500/30' 
                          : 'bg-blue-500/10 border-blue-500/30'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CreativeIcon campaignType="facebook" adName={campaign.campaign_name} />
                            <p className="text-white font-medium">{campaign.campaign_name}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${campaign.strategy === 'Retargeting' 
                            ? 'bg-amber-500/20 text-amber-400' 
                            : 'bg-blue-500/20 text-blue-400'}`}>
                            {campaign.strategy}
                          </span>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <p className="text-2xl font-bold text-white">
                              {campaign.impressions >= 1000 ? Math.round(campaign.impressions / 1000) + 'K' : campaign.impressions}
                            </p>
                            <p className="text-slate-500 text-xs">impressions</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-white">{campaign.clicks.toLocaleString()}</p>
                            <p className="text-slate-500 text-xs">clicks</p>
                          </div>
                          <div>
                            <p className={`text-2xl font-bold ${campaign.ctr >= 2 ? 'text-emerald-400' : 'text-white'}`}>
                              {campaign.ctr}%
                            </p>
                            <p className="text-slate-500 text-xs">CTR</p>
                          </div>
                        </div>
                        {campaign.strategy === 'Retargeting' && campaign.ctr > 2 && (
                          <p className="text-amber-400 text-xs mt-2">
                            🎯 Retargeting is working! People who've seen you before are more likely to click.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                </>)}

                {/* Individual Ad Line Items - only show if NO facebookCampaign data (avoids duplication) */}
                {!(facebookCampaign?.has_data && facebookCampaign?.campaigns?.length > 0) && filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta').length > 0 && (
                  <div className="mt-6">
                    <div
                      className="flex items-center justify-between px-4 py-3 bg-white/5 rounded-xl border border-white/10 cursor-pointer hover:bg-white/10 transition-colors mb-3"
                      onClick={() => setExpandedCampaigns(prev => ({ ...prev, meta_ad_list: !prev.meta_ad_list }))}
                      data-testid="meta-ad-list-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedCampaigns.meta_ad_list ? '' : '-rotate-90'}`} />
                        <p className="text-white font-semibold text-sm">Click to View: Ad Creative and Performance by line</p>
                      </div>
                      <span className="text-xs text-slate-500">{filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta').length} ads</span>
                    </div>
                    {expandedCampaigns.meta_ad_list && (
                      <div className="space-y-2">
                        {filteredAdMetrics.filter(a => a.platform === 'facebook' || a.platform === 'meta').map((ad, idx) => (
                          <div key={ad.id || idx} className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-2.5 border border-white/10">
                            <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
                              <CreativeIcon campaignType="facebook" adName={ad.ad_name} />
                              <span className="text-slate-300 text-sm truncate">{ad.ad_name}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs flex-shrink-0">
                              <span className="text-blue-400">{(ad.impressions || 0).toLocaleString()} imp</span>
                              {(ad.clicks > 0 || ad.link_clicks > 0) && <span className="text-cyan-400">{(ad.link_clicks || ad.clicks || 0).toLocaleString()} clk</span>}
                              {ad.ctr > 0 && <span className="text-amber-400">{ad.ctr}% CTR</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </>)}
            </CollapsibleCard>
          ) : null}

          {/* MOBILE CONQUESTING CAMPAIGN PERFORMANCE */}
          {mobileSummary && mobileSummary.overview && mobileSummary.overview.total_impressions > 0 && (
            <CollapsibleCard
              testId="mobile-conquesting-section"
              story={channelStories.mobile}
              containerClass="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-2xl p-6 border border-cyan-500/20"
              chevronClass="text-cyan-300"
              header={
                <SectionReportHeader
                  clientName={localClient.name}
                  platformLabel="Mobile"
                  reportTitle="Mobile Display Ads"
                  reportStart={localClient.report_start}
                  reportEnd={localClient.report_end}
                  accentColor="#22d3ee"
                  iconBgClass="bg-gradient-to-br from-cyan-500 to-teal-500"
                  icon={<span className="text-2xl">📱</span>}
                  rightContent={null}
                />
              }
            >
                {localClient.client_type !== "multi" ? (
                  <MobileStoryLayout clientName={localClient.name} mobileSummary={mobileSummary} CreativeIcon={CreativeIcon} />
                ) : (<>

                {/* Verified Visits Hero — the killer mobile metric */}
                {mobileSummary.overview.verified_visits > 0 && (
                  <div className="bg-gradient-to-r from-cyan-500/15 to-emerald-500/15 rounded-xl p-5 border border-cyan-500/25 mb-6">
                    <div className="flex items-center gap-5">
                      <div className="flex-shrink-0 w-20 h-20 rounded-full bg-cyan-500/20 flex items-center justify-center border-2 border-cyan-500/30">
                        <span className="text-3xl">🚶</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-cyan-400 text-[11px] font-semibold uppercase tracking-wider mb-1">Verified Walk-In Visits</p>
                        <p className="text-white text-3xl font-bold">{mobileSummary.overview.verified_visits.toLocaleString()}</p>
                        <p className="text-slate-300 text-sm mt-1">
                          <span className="font-semibold text-white">{mobileSummary.overview.verified_visits.toLocaleString()} people</span> saw {localClient.name}'s mobile ad and then <span className="text-cyan-400 font-semibold">physically walked into the business</span>.
                        </p>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                          That's only the visits we can verify with GPS. The estimated total is <span className="text-white font-semibold">{mobileSummary.overview.estimated_visits?.toLocaleString() || (mobileSummary.overview.verified_visits * 4).toLocaleString()}</span>, because only about 1 in 4 visitors have GPS active when they walk in.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Overview Stats */}
                <div className={`grid ${mobileSummary.overview.verified_visits > 0 ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'} gap-3 mb-6`}>
                  <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <p className="text-2xl font-bold text-cyan-400">
                      {mobileSummary.overview.total_impressions >= 1000000
                        ? (mobileSummary.overview.total_impressions / 1000000).toFixed(1) + 'M'
                        : mobileSummary.overview.total_impressions >= 1000 
                          ? Math.round(mobileSummary.overview.total_impressions / 1000) + 'K' 
                          : mobileSummary.overview.total_impressions.toLocaleString()}
                    </p>
                    <p className="text-slate-400 text-xs mt-1">Impressions</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <p className="text-2xl font-bold text-emerald-400">{mobileSummary.overview.total_clicks.toLocaleString()}</p>
                    <p className="text-slate-400 text-xs mt-1">Clicks</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                    <p className="text-2xl font-bold text-amber-400">{mobileSummary.overview.ctr}%</p>
                    <p className="text-slate-400 text-xs mt-1">CTR</p>
                  </div>
                  {mobileSummary.overview.verified_visits > 0 && (
                    <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                      <p className="text-2xl font-bold text-cyan-400">{mobileSummary.overview.verified_visits.toLocaleString()}</p>
                      <p className="text-slate-400 text-xs mt-1">Verified Visits</p>
                    </div>
                  )}
                  {mobileSummary.overview.verified_visits > 0 && (
                    <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                      <p className="text-2xl font-bold text-teal-400">{mobileSummary.overview.visitation_rate || (mobileSummary.overview.verified_visits / mobileSummary.overview.total_impressions * 100).toFixed(2)}%</p>
                      <p className="text-slate-400 text-xs mt-1">Visitation Rate</p>
                    </div>
                  )}
                </div>

                {/* Campaign Performance vs National Benchmarks */}
                {(() => {
                  const overallCtr = parseFloat(mobileSummary.overview.ctr) || 0;
                  const benchmark = 0.07;
                  const multiplier = overallCtr > 0 ? (overallCtr / benchmark).toFixed(1) : '0';
                  const hasVisits = mobileSummary.overview.verified_visits > 0;
                  const hasRetargeting = Object.values(mobileSummary.strategy_breakdown || {}).some(v =>
                    (v.strategies || []).some(s => (s.strategy_name || '').toLowerCase().includes('retarget'))
                  );
                  const retargetStrategy = Object.values(mobileSummary.strategy_breakdown || {}).flatMap(v => v.strategies || []).find(s => (s.strategy_name || '').toLowerCase().includes('retarget'));
                  const retargetCtr = retargetStrategy && retargetStrategy.impressions > 0 ? (retargetStrategy.clicks / retargetStrategy.impressions * 100).toFixed(2) : null;

                  return (
                    <div className="bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 rounded-xl p-5 border border-cyan-500/20 mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">📊</span>
                        <p className="text-white font-semibold text-sm">How This Campaign Compares to the Display Ad Benchmark</p>
                      </div>
                      <p className="text-slate-300 text-sm leading-relaxed mb-3">
                        Standard mobile display ads average about a <span className="text-slate-100 font-semibold">0.07% CTR</span>. Most people scroll right past. {localClient.name}'s mobile campaign is running at <span className="text-emerald-400 font-bold">{overallCtr}%</span>, which is <span className="text-emerald-400 font-bold">{multiplier}x that benchmark</span>. But CTR is just one part of the story. Here's what's really setting this campaign apart:
                      </p>
                      <div className="flex items-start gap-2 bg-white/5 rounded-lg px-3 py-2.5 border border-white/10 mb-4" data-testid="mobile-benchmark-explainer">
                        <Info className="w-4 h-4 text-cyan-300 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-300 text-xs leading-relaxed">
                          <span className="text-white font-semibold">About this benchmark:</span> 0.07% is the long-standing industry figure for banner ads on phones and websites, based on Google&apos;s display ad data. It&apos;s the right comparison here because this campaign runs those same display placements. It is not compared to search ads, where people are already looking for something and naturally click far more. Benchmarks vary by source, so the verified walk-ins and trends in this section are the strongest proof.
                        </p>
                      </div>

                      <div className="space-y-4">
                        {/* Verified Walk-In Visits */}
                        {hasVisits && (
                          <div className="flex items-start gap-4 bg-white/5 rounded-lg p-4 border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">🚶</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-sm">Verified Walk-In Visits</p>
                              <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                                Most display campaigns can only measure clicks. This one measures <span className="text-white font-medium">real people walking through the door</span>. Using GPS verification, we confirmed <span className="text-cyan-400 font-bold">{mobileSummary.overview.verified_visits.toLocaleString()} in-store visits</span> directly tied to ad exposure. The industry considers foot traffic attribution the <span className="text-white font-medium">gold standard</span> for measuring offline ROI. And most campaigns don't even have it.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Geo-Retargeting */}
                        {hasRetargeting && (
                          <div className="flex items-start gap-4 bg-white/5 rounded-lg p-4 border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">🎯</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-sm">Geo-Retargeting</p>
                              <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                                Standard display ads show to broad audiences. This campaign <span className="text-white font-medium">re-engages people who already visited competitor locations</span> by serving them ads after they leave. Industry data shows retargeted users are <span className="text-white font-medium">70% more likely to convert</span> than first-time viewers, with CTRs averaging 0.7-1.2% (vs 0.07% standard).
                                {retargetCtr && <> {localClient.name}'s retargeting is running at <span className="text-violet-400 font-bold">{retargetCtr}% CTR</span>.</>}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Animated & Rich Media Creative */}
                        <div className="flex items-start gap-4 bg-white/5 rounded-lg p-4 border border-white/5">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-lg">✨</span>
                          </div>
                          <div>
                            <p className="text-white font-semibold text-sm">Animated & Multi-Format Creative</p>
                            <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                              Static banners get ignored. {localClient.name}'s campaign runs <span className="text-white font-medium">animated GIFs, scroller ads, and video</span> across 5 different sizes, designed to stop the scroll and catch attention. Rich media ads generate <span className="text-white font-medium">up to 267% higher CTR</span> than standard static banners, and the multi-size approach ensures the ads look native on every screen and placement.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Industry CTR Benchmark — same treatment as Social Mirror */}
                {(() => {
                  const overview = mobileSummary.overview || {};
                  const ctr = parseFloat(overview.ctr || 0);
                  if (!ctr || ctr <= 0) return null;
                  const benchmark = 0.07;
                  const multiplier = (ctr / benchmark).toFixed(1);
                  const isAbove = ctr >= benchmark;
                  const tone = isAbove
                    ? "from-emerald-500/15 to-teal-500/15 border-emerald-500/30"
                    : "from-amber-500/15 to-orange-500/15 border-amber-500/30";
                  const num = isAbove ? "text-emerald-400" : "text-amber-400";
                  return (
                    <div className={`bg-gradient-to-r ${tone} rounded-xl p-4 border mb-6`} data-testid="mobile-industry-benchmark">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{isAbove ? "🏆" : "📊"}</span>
                        <div>
                          <p className="text-white font-semibold">{isAbove ? "Beating the Industry Average!" : "Tracking Industry CTR"}</p>
                          <p className="text-slate-300 text-sm">
                            The benchmark CTR for standard mobile display ads is <span className="text-slate-100 font-semibold">{benchmark}%</span>.
                            {" "}{localClient.name}'s campaign is performing at <span className={`${num} font-bold`}>{multiplier}x</span> that benchmark
                            with a <span className={`${num} font-bold`}>{overview.ctr}% CTR</span>.
                          </p>
                          <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                            CTR alone doesn't tell the whole story for mobile. But combined with <span className="text-slate-300">verified walk-ins</span>, <span className="text-slate-300">geo-targeted impressions</span>, and <span className="text-slate-300">retargeting reach</span>, it's confirmation that the right people are seeing these ads at the right time. A strong CTR on display means the creative is breaking through the noise and the targeting is connecting.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Ad Creative Performance — grouped by Ad Set */}
                {filteredAdMetrics.filter(a => a.platform === 'mobile').length > 0 && (() => {
                  const mobileAds = filteredAdMetrics.filter(a => a.platform === 'mobile');
                  const sizeRegex = /\b(320x50|728x90|300x250|320x480)\b/i;
                  const scrollerRegex = /scroller/i;
                  const setMap = {};
                  mobileAds.forEach(ad => {
                    const name = ad.ad_name || '';
                    let withoutPrefix = name.replace(/^[^_]+_/, '');
                    withoutPrefix = withoutPrefix.replace(/^\d+\.\d+_/, '');
                    const sizeMatch = withoutPrefix.match(sizeRegex);
                    const isScroller = scrollerRegex.test(withoutPrefix);
                    const size = sizeMatch ? sizeMatch[1] : (isScroller ? 'Scroller' : 'Other');
                    const isVideo = /\.(mp4|mov|mp3|avi)$/i.test(withoutPrefix) || /\.(mp4|mov|mp3|avi)$/i.test(name);
                    let setName = withoutPrefix
                      .replace(sizeRegex, '')
                      .replace(/scroller\s*ad/i, '')
                      .replace(/\.(gif|png|jpg|mp4)$/i, '')
                      .replace(/\s*\(\d+\)/, '')
                      .replace(/\s*copy\s*\d*$/i, '')
                      .replace(/\s{2,}/g, ' ')
                      .replace(/[-_.\s]+$/, '')
                      .replace(/^[-_.\s]+/, '')
                      .replace(/[-]\d+$/, '')
                      .trim();
                    if (!setName) setName = withoutPrefix.replace(/\.(gif|png|jpg|mp4)$/i, '').trim();
                    const setKey = setName.toLowerCase();
                    
                    if (!setMap[setKey]) setMap[setKey] = { name: setName, sizes: {}, impressions: 0, clicks: 0, isVideo: false };
                    if (isVideo) setMap[setKey].isVideo = true;
                    setMap[setKey].impressions += ad.impressions || 0;
                    setMap[setKey].clicks += ad.clicks || 0;
                    if (!setMap[setKey].sizes[size]) setMap[setKey].sizes[size] = { impressions: 0, clicks: 0 };
                    setMap[setKey].sizes[size].impressions += ad.impressions || 0;
                    setMap[setKey].sizes[size].clicks += ad.clicks || 0;
                  });
                  const sets = Object.values(setMap).sort((a, b) => b.impressions - a.impressions);
                  const allSizes = ['320x50', '728x90', '300x250', '320x480', 'Scroller'];

                  return (
                    <div className="rounded-xl border border-white/10 overflow-hidden mb-6">
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setExpandedCampaigns(prev => ({ ...prev, mobile_sets: !prev.mobile_sets }))}
                        data-testid="mobile-sets-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedCampaigns.mobile_sets ? '' : '-rotate-90'}`} />
                          <p className="text-white font-medium text-sm">Click to View: Ad Creative Performance</p>
                        </div>
                        <span className="text-xs text-slate-500">{sets.length} ad sets</span>
                      </div>
                      {expandedCampaigns.mobile_sets && (
                        <div className="px-4 pb-4 space-y-3">
                          {sets.map((set, si) => {
                            const setCtr = set.impressions > 0 ? (set.clicks / set.impressions * 100).toFixed(2) : '0.00';
                            return (
                              <div key={si} className="bg-white/5 rounded-lg p-4 border border-white/5">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <CreativeIcon campaignType="mobile_conquesting" adName={set.name} />
                                    <p className="text-white text-sm font-semibold"><span className="text-slate-500 font-normal">Mobile Ad Set:</span> {set.name}</p>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs">
                                    {set.isVideo && <span className="bg-violet-500/20 text-violet-400 px-2 py-0.5 rounded text-[11px] font-semibold">Video</span>}
                                    <span className="text-cyan-400">{set.impressions.toLocaleString()} imp</span>
                                    <span className="text-teal-400">{set.clicks.toLocaleString()} clk</span>
                                    <span className="text-amber-400 font-semibold">{setCtr}%</span>
                                  </div>
                                </div>
                                {set.isVideo && (
                                  <p className="text-slate-400 text-sm mb-3 italic">~5-10% of inventory is available for 15-second horizontal video content</p>
                                )}
                                {!(Object.keys(set.sizes).length === 1 && (set.sizes['Scroller'] || set.sizes['Other'])) && !set.isVideo && (
                                <div className="grid grid-cols-4 gap-2">
                                  {allSizes.filter(s => s !== 'Scroller').map(size => {
                                    const sizeData = set.sizes[size];
                                    const hasData = sizeData && sizeData.impressions > 0;
                                    return (
                                      <div key={size} className={`rounded p-2 text-center text-[11px] ${hasData ? 'bg-cyan-500/10 border border-cyan-500/15' : 'bg-white/3 border border-white/5 opacity-30'}`}>
                                        <p className="text-slate-400 font-medium">{size}</p>
                                        {hasData && (
                                          <>
                                            <p className="text-white font-semibold mt-1">{sizeData.impressions.toLocaleString()}</p>
                                            <p className="text-slate-500">imp</p>
                                            <p className="text-amber-400 font-semibold mt-0.5">{(sizeData.clicks / sizeData.impressions * 100).toFixed(2)}%</p>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}


                {/* Strategy Breakdown with Visits */}
                {(() => {
                  const stratBreakdown = mobileSummary.strategy_breakdown || {};
                  const allStrategies = Object.values(stratBreakdown).flatMap(v => v.strategies || []);
                  if (allStrategies.length === 0) return null;

                  // Normalize all variants into 4 canonical buckets so the report
                  // always tells the same story regardless of how the platform
                  // labeled the campaigns (e.g. "3.6 Heating/Air Mobile" and
                  // "Heating/Air Mobile" both roll up to "Behavior"). Anything
                  // unrecognized falls back to "Behavior" as the catch-all for
                  // category- and interest-based targeting.
                  const classifyStrategy = (rawName) => {
                    const n = (rawName || "").toLowerCase();
                    if (n.includes("site retarget")) return "Site Retargeting";
                    if (n.includes("geo-retarget") || n.includes("geo retarget")) return "Geo Retargeting";
                    if (n.includes("geo-fenc") || n.includes("geofenc") || n.includes("geo fenc")) return "Geo Fencing";
                    return "Behavior";
                  };

                  const aggMap = {};
                  allStrategies.forEach(s => {
                    const name = classifyStrategy(s.strategy_name);
                    if (!aggMap[name]) aggMap[name] = { name, impressions: 0, clicks: 0, verified_visits: 0, estimated_visits: 0 };
                    aggMap[name].impressions += s.impressions || 0;
                    aggMap[name].clicks += s.clicks || 0;
                    aggMap[name].verified_visits += s.verified_visits || 0;
                    aggMap[name].estimated_visits += s.estimated_visits || 0;
                  });
                  const strategies = Object.values(aggMap).sort((a, b) => b.impressions - a.impressions);
                  const maxImp = Math.max(...strategies.map(s => s.impressions), 1);
                  const colors = ['bg-cyan-500', 'bg-violet-500', 'bg-amber-500', 'bg-pink-500'];
                  const textColors = ['text-cyan-400', 'text-violet-400', 'text-amber-400', 'text-pink-400'];
                  const bgColors = ['bg-cyan-500/10 border-cyan-500/20', 'bg-violet-500/10 border-violet-500/20', 'bg-amber-500/10 border-amber-500/20', 'bg-pink-500/10 border-pink-500/20'];

                  const subtitleByCount = {
                    1: "One targeted approach reaching the right people at the right moment.",
                    2: "Two approaches working together to reach the right people at the right moment.",
                    3: "Three approaches working together to reach the right people at the right moment.",
                    4: "Four approaches working together to reach the right people at the right moment.",
                  };
                  const subtitle = subtitleByCount[strategies.length] || `${strategies.length} approaches working together to reach the right people at the right moment.`;

                  return (
                    <div className="mb-6" data-testid="targeting-strategy-performance">
                      {/* Section Intro */}
                      <div className="flex items-start gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/30 to-violet-500/30 border border-white/10 flex items-center justify-center flex-shrink-0">
                          <Target className="w-5 h-5 text-cyan-300" />
                        </div>
                        <div>
                          <p className="text-white font-bold text-base">Targeting Strategy Performance</p>
                          <p className="text-slate-400 text-sm">{subtitle}</p>
                        </div>
                      </div>

                      {/* Strategy Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {strategies.map((s, i) => {
                          const pct = (s.impressions / maxImp * 100);
                          const ctr = s.impressions > 0 ? (s.clicks / s.impressions * 100).toFixed(2) : '0.00';
                          const ctrNum = parseFloat(ctr);
                          const nameLower = s.name.toLowerCase();

                          // Strategy style mapping
                          let StrategyIcon = Target;
                          let accent = { ring: 'ring-violet-400/30', glow: 'from-violet-500/20 to-fuchsia-500/10', icon: 'text-violet-300', iconBg: 'bg-violet-500/20', text: 'text-violet-300', hero: 'text-violet-200' };
                          let tagline = 'Targets the right people wherever they browse.';

                          if (nameLower.includes('retarget')) {
                            StrategyIcon = TrendingUp;
                            accent = { ring: 'ring-emerald-400/30', glow: 'from-emerald-500/25 to-teal-500/10', icon: 'text-emerald-300', iconBg: 'bg-emerald-500/20', text: 'text-emerald-300', hero: 'text-emerald-200' };
                            tagline = 'Re-engages people who already visited your geo-zones: the highest-intent audience.';
                          } else if (nameLower.includes('fenc') || nameLower.includes('geo')) {
                            StrategyIcon = MapPin;
                            accent = { ring: 'ring-amber-400/30', glow: 'from-amber-500/25 to-orange-500/10', icon: 'text-amber-300', iconBg: 'bg-amber-500/20', text: 'text-amber-300', hero: 'text-amber-200' };
                            tagline = 'Virtual boundaries around competitors and hotspots: catches people nearby and ready.';
                          } else if (nameLower.includes('behav') || nameLower.includes('interest')) {
                            StrategyIcon = Sparkles;
                            accent = { ring: 'ring-cyan-400/30', glow: 'from-cyan-500/25 to-blue-500/10', icon: 'text-cyan-300', iconBg: 'bg-cyan-500/20', text: 'text-cyan-300', hero: 'text-cyan-200' };
                            tagline = 'Reaches people by browsing habits and interests that match your ideal customer.';
                          }

                          // Hero metric: verified visits if available, else impressions
                          const hasVisits = s.verified_visits > 0;
                          const heroValue = hasVisits ? s.verified_visits : s.impressions;
                          const heroLabel = hasVisits ? 'Verified Walk-In Visits' : 'Impressions Delivered';

                          const formatNum = (n) => {
                            if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
                            if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
                            return n.toLocaleString();
                          };

                          return (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 16 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.08, duration: 0.4 }}
                              className={`relative overflow-hidden rounded-2xl ring-1 ${accent.ring} bg-slate-900/40`}
                              data-testid={`mobile-strategy-${i}`}
                            >
                              {/* Gradient glow */}
                              <div className={`absolute inset-0 bg-gradient-to-br ${accent.glow} pointer-events-none`} />
                              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5 blur-3xl pointer-events-none" />

                              <div className="relative p-5">
                                {/* Header */}
                                <div className="flex items-start gap-3 mb-4">
                                  <div className={`w-11 h-11 rounded-xl ${accent.iconBg} flex items-center justify-center flex-shrink-0 ring-1 ring-white/10`}>
                                    <StrategyIcon className={`w-5 h-5 ${accent.icon}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`${accent.text} text-[11px] font-bold uppercase tracking-wider`}>Strategy {i + 1}</p>
                                    <p className="text-white font-bold text-base leading-tight">{s.name}</p>
                                  </div>
                                  <div className="relative group">
                                    <button className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors" data-testid={`strategy-info-${i}`}>
                                      <Info className="w-3.5 h-3.5 text-slate-400" />
                                    </button>
                                    <div className="absolute z-50 top-full right-0 mt-2 w-64 bg-slate-800 border border-white/15 rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                      <p className="text-white text-xs font-semibold mb-1">{s.name}</p>
                                      <p className="text-slate-300 text-xs leading-relaxed">
                                        {nameLower.includes('retarget') ? (
                                          <>People who have already been to your geo-fenced locations get served ads again: keeping your brand top of mind after they leave. This is one of the most effective strategies because these people have already shown real-world interest in businesses like yours.</>
                                        ) : nameLower.includes('fencing') || nameLower.includes('geo-fenc') ? (
                                          <>Draws a virtual boundary around competitor and high-traffic locations. Anyone who enters that zone with their smartphone sees your ad. This targets people who are physically near businesses related to yours: they're already in the neighborhood and in the mindset to buy.</>
                                        ) : (
                                          <>Targets people based on their interests and browsing behavior: like visiting health food websites, searching for restaurants, or reading dining reviews. These are people whose online habits match the profile of your ideal customer.</>
                                        )}
                                      </p>
                                      <div className="absolute top-0 right-4 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-t border-white/15" />
                                    </div>
                                  </div>
                                </div>

                                {/* Tagline */}
                                <p className="text-slate-400 text-xs leading-relaxed mb-4 min-h-[32px]">{tagline}</p>

                                {/* Hero metric */}
                                <div className="mb-4">
                                  <p className={`text-4xl font-black tracking-tight ${accent.hero}`}>{formatNum(heroValue)}</p>
                                  <p className="text-slate-500 text-xs font-medium mt-0.5">{heroLabel}</p>
                                </div>

                                {/* Share of voice bar */}
                                <div className="mb-4">
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-slate-500 text-[11px] uppercase tracking-wider">Share of Impressions</span>
                                    <span className={`${accent.text} text-xs font-bold`}>{Math.round(pct)}%</span>
                                  </div>
                                  <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{ width: `${pct}%` }}
                                      transition={{ delay: i * 0.08 + 0.2, duration: 0.8, ease: 'easeOut' }}
                                      className={`h-2 rounded-full bg-gradient-to-r ${accent.glow.replace(/\/\d+/g, '')} ${accent.iconBg}`}
                                    />
                                  </div>
                                </div>

                                {/* Stat chips */}
                                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
                                  <div className="flex flex-col items-center text-center">
                                    <Eye className="w-3.5 h-3.5 text-slate-500 mb-1" />
                                    <span className="text-white text-xs font-bold leading-none">{formatNum(s.impressions)}</span>
                                    <span className="text-slate-500 text-[11px] mt-1">Impressions</span>
                                  </div>
                                  <div className="flex flex-col items-center text-center border-x border-white/5">
                                    <MousePointer className="w-3.5 h-3.5 text-slate-500 mb-1" />
                                    <span className="text-white text-xs font-bold leading-none">{formatNum(s.clicks)}</span>
                                    <span className="text-slate-500 text-[11px] mt-1">Clicks</span>
                                  </div>
                                  <div className="flex flex-col items-center text-center">
                                    <TrendingUp className={`w-3.5 h-3.5 mb-1 ${ctrNum >= 0.5 ? 'text-emerald-400' : 'text-slate-500'}`} />
                                    <span className={`text-xs font-bold leading-none ${ctrNum >= 0.5 ? 'text-emerald-300' : 'text-white'}`}>{ctr}%</span>
                                    <span className="text-slate-500 text-[11px] mt-1">CTR</span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Geo-Fencing Hotspots */}
                {mobileSummary?.geofencing_locations && mobileSummary.geofencing_locations.length > 0 && (
                  <div className="rounded-xl border border-white/10 overflow-hidden">
                    <div className="px-4 py-3">
                      <p className="text-white font-semibold text-sm">Geo-Fencing Hotspots</p>
                      <p className="text-slate-500 text-xs">Top locations by impressions & engagement{mobileSummary.geofencing_locations[0]?.city ? ` in ${mobileSummary.geofencing_locations[0].city}, ${mobileSummary.geofencing_locations[0].state}` : ''}</p>
                    </div>
                    <div className="px-4 pb-4 space-y-2">
                      {[...mobileSummary.geofencing_locations]
                        .sort((a, b) => (b.impressions * b.ctr) - (a.impressions * a.ctr))
                        .filter(loc => loc.impressions > 0 && loc.ctr > 0)
                        .slice(0, 6)
                        .map((loc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5" data-testid={`geofence-hotspot-${idx}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <MapPin className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                              <p className="text-white text-sm font-medium truncate">{loc.address}</p>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0 ml-3">
                              <span className="text-slate-400 text-xs">{loc.impressions.toLocaleString()} impr</span>
                              <span className={`text-xs font-semibold ${loc.ctr >= 0.5 ? 'text-emerald-400' : 'text-slate-300'}`}>
                                {loc.ctr}% CTR
                              </span>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                )}

                </>)}
            </CollapsibleCard>
          )}

          {/* TIKTOK ADS - Comprehensive Section */}
          {(tiktokSummary?.has_data || filteredAdMetrics.filter(a => a.platform === 'tiktok').length > 0) && (
            <TikTokSection
              data={tiktokSummary}
              story={channelStories.tiktok}
              clientName={localClient.name}
              reportStart={localClient.report_start}
              reportEnd={localClient.report_end}
              onViewCreative={(adName) => {
                const creative = getCreativeForType('tiktok', adName);
                if (creative) {
                  setPreviewCreative(creative);
                } else {
                  toast.info(`No creative linked to "${adName}" yet.`, {
                    description: "Click Ad Creative in the sidebar to link one.",
                    action: onViewChange ? { label: "Open Ad Creative", onClick: () => onViewChange("ad-creative") } : undefined,
                  });
                }
              }}
            />
          )}

          {/* SOCIAL MIRROR / DISPLAY ADS */}
          {socialMirrorSummary && socialMirrorSummary.overview?.total_impressions > 0 && (
            <CollapsibleCard
              testId="social-mirror-section"
              story={channelStories.socialMirror}
              containerClass="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-2xl p-6 border border-cyan-500/20"
              chevronClass="text-cyan-300"
              header={
                <SectionReportHeader
                  clientName={localClient.name}
                  platformLabel="Social Mirror"
                  reportTitle="Social Mirror Display Ads"
                  reportStart={localClient.report_start}
                  reportEnd={localClient.report_end}
                  accentColor="#22d3ee"
                  iconBgClass="bg-gradient-to-br from-cyan-500 to-teal-500"
                  icon={<span className="text-2xl">🪞</span>}
                />
              }
            >
                <SocialMirrorDaily campaigns={socialMirrorSummary.campaigns} />
                {localClient.client_type !== "multi" ? (
                  <SocialMirrorStoryLayout clientName={localClient.name} socialMirrorSummary={socialMirrorSummary} CreativeIcon={CreativeIcon}
                    clientId={localClient.id} API={API} readOnly={readOnly} reportStart={localClient.report_start} reportEnd={localClient.report_end} />
                ) : (<>

                {/* Device Breakdown - FIRST */}
                {socialMirrorSummary.device_breakdown && Object.keys(socialMirrorSummary.device_breakdown).length > 0 && (
                  <div className="bg-white/5 rounded-xl p-5 border border-white/10 mb-6">
                    <p className="text-white font-semibold mb-4">Devices Where Your Ads Appeared</p>
                    <div className="flex justify-center gap-8">
                      {/* Mobile */}
                      {socialMirrorSummary.device_breakdown.Mobile && (
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-16 text-cyan-400 mb-2">
                            <svg className="w-full h-full" viewBox="0 0 60 90" fill="none">
                              <rect x="5" y="2" width="50" height="86" rx="8" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.8"/>
                              <rect x="10" y="10" width="40" height="60" rx="2" fill="currentColor" opacity="0.3"/>
                              <circle cx="30" cy="78" r="4" fill="currentColor" opacity="0.6"/>
                            </svg>
                          </div>
                          <span className="text-white font-medium text-sm">Mobile</span>
                          <span className="text-cyan-400 text-lg font-bold">{socialMirrorSummary.device_breakdown.Mobile.impressions?.toLocaleString() || 0}</span>
                          {socialMirrorSummary.device_breakdown.Mobile.ctr > 0 ? (
                            <span className="text-slate-400 text-xs">{socialMirrorSummary.device_breakdown.Mobile.ctr}% CTR</span>
                          ) : (
                            <span className="text-slate-500 text-xs">{socialMirrorSummary.overview?.total_impressions > 0 ? ((socialMirrorSummary.device_breakdown.Mobile.impressions / socialMirrorSummary.overview.total_impressions) * 100).toFixed(1) : 0}% Impressions</span>
                          )}
                        </div>
                      )}
                      {/* Desktop */}
                      {socialMirrorSummary.device_breakdown.Desktop && (
                        <div className="flex flex-col items-center">
                          <div className="w-20 h-16 text-cyan-400 mb-2">
                            <svg className="w-full h-full" viewBox="0 0 120 90" fill="none">
                              <rect x="10" y="5" width="100" height="60" rx="4" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.6"/>
                              <rect x="16" y="11" width="88" height="48" rx="2" fill="currentColor" opacity="0.3"/>
                              <rect x="45" y="65" width="30" height="5" rx="1" fill="currentColor" opacity="0.6"/>
                              <rect x="30" y="72" width="60" height="8" rx="2" fill="currentColor" opacity="0.5"/>
                            </svg>
                          </div>
                          <span className="text-white font-medium text-sm">Desktop</span>
                          <span className="text-cyan-400 text-lg font-bold">{socialMirrorSummary.device_breakdown.Desktop.impressions?.toLocaleString() || 0}</span>
                          {socialMirrorSummary.device_breakdown.Desktop.ctr > 0 ? (
                            <span className="text-slate-400 text-xs">{socialMirrorSummary.device_breakdown.Desktop.ctr}% CTR</span>
                          ) : (
                            <span className="text-slate-500 text-xs">{socialMirrorSummary.overview?.total_impressions > 0 ? ((socialMirrorSummary.device_breakdown.Desktop.impressions / socialMirrorSummary.overview.total_impressions) * 100).toFixed(1) : 0}% Impressions</span>
                          )}
                        </div>
                      )}
                      {/* Tablet */}
                      {socialMirrorSummary.device_breakdown.Tablet && (
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-16 text-cyan-400 mb-2">
                            <svg className="w-full h-full" viewBox="0 0 70 90" fill="none">
                              <rect x="5" y="5" width="60" height="80" rx="6" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.6"/>
                              <rect x="10" y="12" width="50" height="60" rx="2" fill="currentColor" opacity="0.3"/>
                              <circle cx="35" cy="80" r="3" fill="currentColor" opacity="0.6"/>
                            </svg>
                          </div>
                          <span className="text-white font-medium text-sm">Tablet</span>
                          <span className="text-cyan-400 text-lg font-bold">{socialMirrorSummary.device_breakdown.Tablet.impressions?.toLocaleString() || 0}</span>
                          {socialMirrorSummary.device_breakdown.Tablet.ctr > 0 ? (
                            <span className="text-slate-400 text-xs">{socialMirrorSummary.device_breakdown.Tablet.ctr}% CTR</span>
                          ) : (
                            <span className="text-slate-500 text-xs">{socialMirrorSummary.overview?.total_impressions > 0 ? ((socialMirrorSummary.device_breakdown.Tablet.impressions / socialMirrorSummary.overview.total_impressions) * 100).toFixed(1) : 0}% Impressions</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Overview Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-400">{socialMirrorSummary.overview.total_impressions?.toLocaleString()}</p>
                    <p className="text-slate-400 text-xs">Impressions</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-teal-400">{socialMirrorSummary.overview.total_clicks?.toLocaleString()}</p>
                    <p className="text-slate-400 text-xs">Clicks</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{socialMirrorSummary.overview.ctr}%</p>
                    <p className="text-slate-400 text-xs">CTR</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{socialMirrorSummary.overview.click_conversions || 0}</p>
                    <p className="text-slate-400 text-xs">Click Conversions</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-pink-400">{socialMirrorSummary.overview.view_through_conversions || 0}</p>
                    <p className="text-slate-400 text-xs">View-through Conv.</p>
                  </div>
                </div>

                {/* Industry CTR Benchmark */}
                {socialMirrorSummary.overview.ctr && parseFloat(socialMirrorSummary.overview.ctr) > 0 && (
                  <div className="bg-gradient-to-r from-emerald-500/15 to-teal-500/15 rounded-xl p-4 border border-emerald-500/30 mt-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🏆</span>
                      <div>
                        <p className="text-white font-semibold">Beating the Industry Average!</p>
                        <p className="text-slate-300 text-sm">
                          The industry average CTR for display ads is <span className="text-slate-100 font-semibold">0.07%</span>. 
                          Your ads are performing at <span className="text-emerald-400 font-bold">{(parseFloat(socialMirrorSummary.overview.ctr) / 0.07).toFixed(1)}x</span> that benchmark 
                          with a <span className="text-emerald-400 font-bold">{socialMirrorSummary.overview.ctr}% CTR</span>.
                        </p>
                        <p className="text-slate-400 text-xs mt-2 leading-relaxed">
                          A high CTR is more than just clicks: it's confirmation that the <span className="text-slate-300">right people</span> are seeing your ads at the <span className="text-slate-300">right time and frequency</span>. When audiences click, it means the creative is resonating and the targeting is working. This is the signal that your brand message is landing with the people most likely to become customers.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Top Creatives */}
                {socialMirrorSummary.top_creatives && socialMirrorSummary.top_creatives.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white font-semibold mb-3">Top Performing Creatives</p>
                    <div className="space-y-2">
                      {socialMirrorSummary.top_creatives.slice(0, 5).map((creative, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
                            <CreativeIcon campaignType="social_mirror" adName={creative.name} />
                            <span className="text-slate-300 text-sm truncate">{creative.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-cyan-400">{creative.impressions?.toLocaleString()} imp</span>
                            <span className="text-teal-400">{creative.clicks?.toLocaleString()} clicks</span>
                            <span className="text-emerald-400 font-semibold">{creative.ctr}% CTR</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>)}
            </CollapsibleCard>
          )}

          {/* GOOGLE ADS - Paid Search Performance */}
          <GoogleAdsSection googleAdsData={googleAdsData} story={channelStories.googleAds} />

          {/* PMAX: isolated pmax_reports, single-location clients only */}
          {localClient.client_type !== "multi" && (
            <PmaxStoryCard records={pmaxInRange} clientName={localClient.name} story={channelStories.pmax} />
          )}

          {/* META CONTENT EFFECT — organic + boosted content performance
              from Meta Business Suite. Completely isolated from paid ad
              metrics (meta_campaigns/ad_metrics). Only renders if the
              agency has uploaded at least one Content Effect record. */}
          <ContentEffectCard records={contentEffectRecords} clientName={client.name} />

          {/* APP FRONT - Push, Email & SMS Campaigns */}
          <AppFrontSection appfrontData={appfrontData} story={channelStories.appfront} />

          {/* CONNECTED TV — shown when the client runs CTV ads */}
          {ctvPublishers.length > 0 && (
          <CollapsibleCard
              testId="ctv-section"
              story={channelStories.ctv}
              containerClass="bg-gradient-to-r from-purple-600/30 via-indigo-600/20 to-purple-600/30 rounded-2xl p-8 border border-purple-400/30"
              chevronClass="text-purple-300"
              header={
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-purple-500/30 flex items-center justify-center flex-shrink-0 border border-purple-400/30 overflow-hidden">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/3f4daf81-f9d6-4a75-9567-8165a2a20315/images/975d3e2d94b0a05299658eb1087129d3bba13911501f15d850b56dae71b4113e.png" 
                      alt="Smart TV" 
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-purple-300 text-xs font-semibold uppercase tracking-wider mb-1">The Power of</p>
                    <h3 className="text-3xl font-bold text-white">Connected TV</h3>
                    {hasReportPeriod && (
                      <p className="text-slate-400 text-xs mt-1">
                        {format(new Date(localClient.report_start + 'T00:00:00'), "MMM d, yyyy")} – {format(new Date(localClient.report_end + 'T00:00:00'), "MMM d, yyyy")}
                      </p>
                    )}
                  </div>
                </div>
              }
            >
                {/* Single "View Creative" eye-icon — same pattern as Meta /
                    Mobile / Social Mirror. Opens the first ad_creative tagged
                    Connected TV in the AdPreviewModal slide-out drawer.
                    Falls back to nudging the agency to add a creative on the
                    Ad Creative page when none exist. */}
                <div className="mb-6 flex items-center" data-testid="ctv-view-creative-cta">
                  <ViewCreativeButton campaignType="ctv" />
                </div>

                <CtvMonths months={ctvMonths} />
                <CtvTiles publishers={ctvPublishers} conversions={ctvConversions} />

              {/* WHERE YOUR CTV ADS RAN - Publisher List */}
              <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-2xl p-6 border border-purple-500/20 mt-6">
                <p className="text-purple-400 text-xs font-semibold uppercase tracking-wider mb-1">Where Your CTV Ads Ran</p>
                {hasReportPeriod && (
                  <p className="text-slate-400 text-xs mb-4">{format(new Date(localClient.report_start + 'T00:00:00'), "MMM d, yyyy")} – {format(new Date(localClient.report_end + 'T00:00:00'), "MMM d, yyyy")}</p>
                )}
                {!hasReportPeriod && <div className="mb-4" />}
                
                <p className="text-lg text-white mb-2">
                  Your Connected TV ads appeared on <span className="text-purple-400 font-bold">{ctvPublishers.filter(p => !p.is_other).length} streaming platforms</span>.
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  These are the apps and services where viewers watched your ads on their TV screens: reaching them in their living rooms during premium content.
                </p>

                {/* Publisher List - Compact Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {ctvPublishers.map((pub, index) => {
                    const maxImpressions = ctvPublishers[0]?.impressions || 1;
                    const barWidth = (pub.impressions / maxImpressions) * 100;
                    
                    // Map publisher names to their brand logos
                    const publisherLogos = {
                      'Sling TV': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/wdj3oa57_sling%20tv%20square.png',
                      'Sling TV - Live Sports, News, Shows + Freestream': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/wdj3oa57_sling%20tv%20square.png',
                      'The Roku Channel': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/cw4kjhw9_Roku-Symbol.png',
                      'Amazon Prime Video': 'https://customer-assets.emergentagent.com/job_3f4daf81-f9d6-4a75-9567-8165a2a20315/artifacts/pm8h8zsm_prime.webp',
                      'Prime Video': 'https://customer-assets.emergentagent.com/job_3f4daf81-f9d6-4a75-9567-8165a2a20315/artifacts/pm8h8zsm_prime.webp',
                      'Pluto TV': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/hfyh85f7_pluto%20TV.png',
                      'DISH Anywhere': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/02wy4kp6_dish%20anywhere%20logo.png',
                      'Dish Anywhere': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/02wy4kp6_dish%20anywhere%20logo.png',
                      'Philo': 'https://customer-assets.emergentagent.com/job_0daae5c5-f980-4f43-919d-7ceb55643735/artifacts/0t7d0fwk_Philo_logo.png',
                      'DIRECTV': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/zh2k5wc4_directTV%20stream.jpeg',
                      'DirecTV Stream': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/zh2k5wc4_directTV%20stream.jpeg',
                      'DIRECTV Stream': 'https://customer-assets.emergentagent.com/job_b3b79f4c-1f85-44ed-8fa7-07353842b4da/artifacts/zh2k5wc4_directTV%20stream.jpeg',
                      'Tubi': 'https://customer-assets.emergentagent.com/job_0daae5c5-f980-4f43-919d-7ceb55643735/artifacts/p3jlabaf_tubi.png',
                      'Tubi - Free Movies & TV': 'https://customer-assets.emergentagent.com/job_0daae5c5-f980-4f43-919d-7ceb55643735/artifacts/p3jlabaf_tubi.png',
                      'Telly': 'https://customer-assets.emergentagent.com/job_0daae5c5-f980-4f43-919d-7ceb55643735/artifacts/fmilsmy7_telly.png',
                      'Telly - The Truly Smart TV': 'https://customer-assets.emergentagent.com/job_0daae5c5-f980-4f43-919d-7ceb55643735/artifacts/fmilsmy7_telly.png',
                      'Samsung TV Plus': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/7o77goyi_Samsung_TV_Plus_main1.jpg',
                      'SAMSUNG TV Plus': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/7o77goyi_Samsung_TV_Plus_main1.jpg',
                      'Vizio WatchFree+': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/bo8p74th_vizio.jpg',
                      'Vizio': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/bo8p74th_vizio.jpg',
                      'Vizio - Vizio WatchFree+': 'https://customer-assets.emergentagent.com/job_6f75df89-405a-4cf8-ab5f-a582c321d0a0/artifacts/bo8p74th_vizio.jpg',
                    };
                    
                    const logoUrl = publisherLogos[pub.publisher_name];
                    const isOther = pub.is_other || pub.publisher_name === "Other / Unspecified";

                    return (
                      <div
                        key={pub.publisher_name}
                        className={`rounded-lg p-2 border ${isOther ? 'bg-purple-500/5 border-purple-500/15 border-dashed' : 'bg-white/5 border-white/10'}`}
                        title={isOther ? "Premium CTV inventory packages: buys delivered through programmatic exchanges and inventory bundles where the publisher isn't broken out individually." : undefined}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          {isOther ? (
                            <div className="w-7 h-7 rounded bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-purple-300" />
                            </div>
                          ) : logoUrl ? (
                            <div className="w-7 h-7 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                              <img src={logoUrl} alt={pub.publisher_name} className="w-7 h-7 object-cover rounded" />
                            </div>
                          ) : (
                            <div className="w-7 h-7 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                              <Tv className="w-4 h-4 text-purple-300" />
                            </div>
                          )}
                          <CtvPublisherName
                            name={pub.publisher_name}
                            isOther={isOther}
                            editable={!readOnly}
                            API={API}
                            clientId={client.id}
                            reportStart={localClient.report_start}
                            reportEnd={localClient.report_end}
                            location={activeLocation}
                            onRenamed={fetchCtvData}
                            testId={`ctv-publisher-${index}`}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-purple-400 font-semibold">{pub.impressions.toLocaleString()}</span>
                          {!isOther && <span className="text-emerald-400">{pub.completion_rate}%</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>
            </CollapsibleCard>
          )}

          {/* TOP-OF-FUNNEL BRANDING CHANNELS — one collapsible per channel
              (Local TV, Local Radio, etc.). Each one mirrors the Meta/Mobile/CTV
              dropdown shape so they sit naturally alongside digital channels.
              Numeric data (impressions, local_spots) is OPTIONAL: only renders
              when populated. Brief intro paragraph keeps the story honest:
              "not every touchpoint can be measured in clicks." */}
          {filteredBrandChannels.length > 0 && (
            <div className="bg-gradient-to-br from-amber-500/8 to-orange-500/8 rounded-2xl p-5 border border-amber-500/15 mb-6" data-testid="brand-channels-intro">
              <p className="text-amber-300/90 text-[11px] font-bold uppercase tracking-[0.2em] mb-2">Off-Screen Brand Lift</p>
              <p className="text-slate-300 text-sm leading-relaxed">
                Not every touchpoint can be measured in clicks. <span className="text-white font-medium">Local TV, Local Radio</span>, and similar broadcast channels build the brand recognition that makes {localClient.name}'s digital ads perform better: when someone sees a digital ad after hearing the radio spot or seeing the TV commercial, they're far more likely to engage.
              </p>
            </div>
          )}

          {filteredBrandChannels.map((ch, idx) => {
            const nameLower = (ch.name || "").toLowerCase();
            const isTv = nameLower.includes("tv");
            const isRadio = nameLower.includes("radio") || nameLower.includes("audio");
            // Pick an icon glyph based on channel type — keeps it visually distinct from CTV.
            const iconSvg = isTv ? (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="15" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg>
            ) : isRadio ? (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/></svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/></svg>
            );
            const kicker = isTv ? "Broadcast television" : isRadio ? "Broadcast radio" : "Brand channel";
            const defaultBlurb = isTv
              ? `Local TV spots build broad neighborhood awareness for ${localClient.name}: the kind of visibility that makes digital ads feel familiar before someone clicks.`
              : isRadio
              ? `Local radio keeps ${localClient.name} top of mind during commute and drive-time hours, when listeners are most receptive to local-business messaging.`
              : `An off-screen channel that builds awareness for ${localClient.name} alongside the digital campaigns above.`;
            return (
              <CollapsibleCard
                key={`brand-channel-${idx}`}
                testId={`brand-channel-section-${idx}`}
                containerClass="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 border border-amber-500/20"
                chevronClass="text-amber-300"
                header={
                  <SectionReportHeader
                    clientName={localClient.name}
                    platformLabel={kicker}
                    reportTitle={ch.name}
                    reportStart={ch.report_start || localClient.report_start}
                    reportEnd={ch.report_end || localClient.report_end}
                    accentColor="#fbbf24"
                    iconBgClass="bg-amber-600"
                    icon={iconSvg}
                  />
                }
              >
                <div className="space-y-4">
                  {/* Status pill */}
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ch.status === 'Active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>
                      {ch.status || 'Active'}
                    </span>
                  </div>

                  {/* Description / story */}
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {ch.description || defaultBlurb}
                  </p>

                  {/* Optional metrics — only render if populated */}
                  {(ch.impressions || ch.local_spots) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {ch.impressions && (
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <p className="text-amber-400 text-2xl font-bold tabular-nums">{parseInt(ch.impressions).toLocaleString()}</p>
                          <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Impressions</p>
                        </div>
                      )}
                      {ch.local_spots && (
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <p className="text-amber-400 text-2xl font-bold tabular-nums">{parseInt(ch.local_spots).toLocaleString()}</p>
                          <p className="text-slate-400 text-[11px] uppercase tracking-wider mt-1">Local Spots</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {ch.notes && (
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <p className="text-slate-300 text-xs italic leading-relaxed">{ch.notes}</p>
                    </div>
                  )}

                  {/* Empty-state hint when nothing extra has been entered yet */}
                  {!ch.description && !ch.impressions && !ch.local_spots && !ch.notes && (
                    <p className="text-slate-500 text-xs italic">
                      More detail can be added later: campaign metrics, spot counts, or audience notes.
                    </p>
                  )}
                </div>
              </CollapsibleCard>
            );
          })}

          </ChapterDropdown>

          {/* CHAPTER 03 (DROPDOWN): WHERE WE RAN — Campaign Details inside */}
          <ChapterDropdown
            id="chapter-where"
            number="03"
            kicker="Geography & targeting"
            title="Where We Ran"
            prompt={`The physical neighborhoods and online zones ${localClient.name}'s ads showed up in.`}
            accent="amber"
          >

          {/* Campaign Details - Geo & Targeting */}
          <div className="relative z-10 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column — Map + Geofencing */}
              <div>
              {/* Where Your Ads Run */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <p className="text-white font-medium">Where Your Ads Run</p>
                  </div>
                  {editingField !== 'geo' && !readOnly && (
                    <button 
                      onClick={() => startEditing('geo')}
                      className="text-slate-400 hover:text-white transition-colors p-1 rounded"
                      data-testid="edit-geo-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {editingField === 'geo' ? (
                  <div className="space-y-2">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="e.g., Chicago Metro Area, Northern Illinois"
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                      autoFocus
                      data-testid="geo-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={saveField}
                        disabled={savingField}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        data-testid="save-geo-btn"
                      >
                        {savingField ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditing}
                        className="text-slate-400 hover:text-white text-xs"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (localClient.geo_targeting || geofenceLocs.length > 0 || allTargetZipcodes.length > 0) ? (
                  <div>
                    {localClient.geo_targeting && <p className="text-slate-300 text-sm mb-3">{localClient.geo_targeting}</p>}
                    <GeoMap location={localClient.geo_targeting || (geofenceLocs[0]?.city ? `${geofenceLocs[0].city}, ${geofenceLocs[0].state || ''}` : '')} geofencingLocations={geofenceLocs} targetZipcodes={allTargetZipcodes} API={API} onTypes={setMapTypes} />
                    <div className="flex items-center flex-wrap gap-4 mt-2 text-xs text-slate-400" data-testid="map-legend">
                      {mapTypes.has('city') && <span className="flex items-center gap-1.5" data-testid="legend-city"><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 border border-white/50"></span> City targets</span>}
                      {mapTypes.has('geofence') && <span className="flex items-center gap-1.5" data-testid="legend-geofence"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 border border-white/50"></span> Geofencing hotspots</span>}
                      {allTargetZipcodes.length > 0 && <span className="flex items-center gap-1.5" data-testid="legend-zip"><span className="inline-block w-2.5 h-2.5 rounded-full bg-violet-500/60 border border-violet-400/50"></span> Target zip codes</span>}
                      {mapTypes.has('radius') && <span className="flex items-center gap-1.5" data-testid="legend-radius"><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500/40 border border-blue-400/50 border-dashed"></span> Radius target</span>}
                      <span className="ml-auto opacity-70">Tap the map for details</span>
                    </div>
                    <ZipTownList API={API} zips={allTargetZipcodes} />
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm italic">Click the pencil to add geographic targeting</p>
                )}
              </div>
            </div>

            {/* Right column — always shows Programmatic Targeting */}
            <div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                      <Users className="w-4 h-4 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">How We Find Your Potential Customers</p>
                      <p className="text-slate-500 text-[11px]">Finding the right people, at the right time, on the right device</p>
                    </div>
                    {editingField !== 'audience' && !readOnly && (
                      <button 
                        onClick={() => startEditing('audience')}
                        className="text-slate-400 hover:text-white transition-colors p-1 rounded ml-auto"
                        data-testid="edit-audience-btn"
                        title="Edit targeting description"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {editingField === 'audience' ? (
                    <div className="space-y-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="e.g., Homeowners 35-65, interested in home improvement"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                        autoFocus
                        data-testid="audience-input"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveField} disabled={savingField} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="save-audience-btn">
                          {savingField ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEditing} className="text-slate-400 hover:text-white text-xs">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Targeting for this client */}
                      {localClient.audience_targeting && (
                        <div className="bg-violet-500/10 rounded-lg p-4 border border-violet-500/20 mb-4">
                          <p className="text-violet-400 text-[11px] font-semibold uppercase tracking-wider mb-1">Who We're Looking For: {localClient.name}</p>
                          <p className="text-white text-sm leading-relaxed">{localClient.audience_targeting}</p>
                        </div>
                      )}

                      {/* How it works — simple overview */}
                      <div className="space-y-3 mb-4">
                        <p className="text-slate-300 text-sm leading-relaxed">
                          Every person leaves a digital footprint. Programmatic advertising reads those signals to find the people who look most like your ideal customer.
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                            <div className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                              <Search className="w-4 h-4 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-white text-xs font-medium">What they search for</p>
                              <p className="text-slate-400 text-[11px]">Someone Googling "best restaurants near me" or "happy hour Charlottesville" signals they're actively in the market.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                            <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                              <ShoppingBag className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-white text-xs font-medium">What they buy</p>
                              <p className="text-slate-400 text-[11px]">Purchase history reveals spending habits: people who regularly buy dining, entertainment, or lifestyle products match your customer profile.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                            <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-4 h-4 text-amber-400" />
                            </div>
                            <div>
                              <p className="text-white text-xs font-medium">Where they go</p>
                              <p className="text-slate-400 text-[11px]">Location data shows who visits competitor businesses, nearby shopping areas, and local events: people already in your neighborhood and market.</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-slate-400 text-xs leading-relaxed">
                          When someone's search history, purchase behavior, and location patterns all line up with the customer you're looking for: that's when your ad gets shown. It's not random. It's a <span className="text-white font-medium">match</span>.
                        </p>
                      </div>

                      {/* Geo + demo constraint */}
                      <div className="bg-gradient-to-r from-violet-500/5 to-indigo-500/5 rounded-lg p-4 border border-violet-500/10">
                        <p className="text-slate-300 text-xs leading-relaxed">
                          Every campaign runs within the <span className="text-white font-medium">geographic areas and demographic profiles</span> we've selected specifically for your business. So every impression is targeted, not wasted.
                        </p>
                      </div>

                      {!localClient.audience_targeting && !readOnly && (
                        <p className="text-slate-500 text-xs italic mt-3">Click the pencil to describe the specific audience you're targeting for this client.</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* TOP-OF-FUNNEL BRANDING CHANNELS — moved to Chapter 02 ("Every Channel, Up Close")
              so each broadcast channel sits alongside Meta/Mobile/CTV as its own collapsible. */}
          </ChapterDropdown>

      {/* CHAPTER 04 (DROPDOWN): THE CONVERSION CHECKLIST — industry-aware funnel walkthrough */}
      {activeLocation === 'all' && (
        <ChapterDropdown
          id="chapter-website-review"
          number="04"
          kicker="After the click"
          title="The Conversion Checklist"
          prompt={`They're visiting ${localClient.name}'s website. Here's what should happen when they get there.`}
          accent="indigo"
        >
          <ConversionChecklist
            clientName={localClient.name}
            industry={localClient.industry || ""}
          />
        </ChapterDropdown>
      )}

      {/* HOW BRAND VALUE COMPOUNDS — flywheel sits right under chapter 04 */}
      {activeLocation === 'all' && <BrandValueFlywheel />}

      {/* CHAPTER 05 (DROPDOWN): LEARN MORE — always shown on every tab, optional extras for curious clients */}
      <ChapterDropdown
        id="chapter-learn-more"
        number="05"
        kicker="For the curious"
        title="Learn More"
        prompt={`How advertising actually works. Extras for when you want to go deeper into ${localClient.name}'s brand exposure story.`}
        accent="blue"
      >
        {/* Intro — always visible inside Chapter 05 */}
        <div className="bg-[#0c465f] rounded-2xl p-5 md:p-6 border border-cyan-400/20 mb-6">
          <p className="text-cyan-200/80 text-[11px] font-bold uppercase tracking-[0.25em] mb-2">A short guide</p>
          <p className="text-white text-base md:text-lg leading-relaxed mb-2">
            The questions every client asks, answered.
          </p>
          <p className="text-slate-300 text-sm leading-relaxed">
            Why run on multiple channels? What does a marketing funnel actually do? How does Connected TV differ from regular TV? The sections below break down how modern advertising compounds into brand value over time.
          </p>
        </div>
        {/* OMNI-CHANNEL ADVERTISING - Comprehensive View with Pie Chart — always shown as educational content */}
          <div className="relative z-10 mb-8">
              <div className="bg-gradient-to-r from-teal-600/30 via-cyan-600/20 to-teal-600/30 rounded-2xl p-8 border border-teal-400/30">
                <button
                  onClick={() => setOmniChannelOpen(v => !v)}
                  data-testid="omni-channel-toggle"
                  className="w-full flex items-center gap-4 text-left group"
                  aria-expanded={omniChannelOpen}
                >
                  <div className="w-16 h-16 rounded-2xl bg-teal-500/30 flex items-center justify-center flex-shrink-0 border border-teal-400/30 overflow-hidden">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/3f4daf81-f9d6-4a75-9567-8165a2a20315/images/975d3e2d94b0a05299658eb1087129d3bba13911501f15d850b56dae71b4113e.png" 
                      alt="Devices" 
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-1">The Power of</p>
                    <h3 className="text-3xl font-bold text-white">Omni-Channel Advertising</h3>
                    <p className="text-teal-300/80 text-xs mt-1 group-hover:text-teal-200 transition-colors">
                      {omniChannelOpen ? 'Click to collapse' : 'Click to learn more'}
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-6 h-6 text-teal-300 flex-shrink-0 transition-transform duration-300 ${omniChannelOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {omniChannelOpen && (
                    <motion.div
                      key="omni-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="pt-6">
                        {/* Customer Journey - Extracted Component */}
                        <CustomerJourney 
                          platformBreakdown={platformBreakdown}
                          marketingChannels={marketingChannels}
                          deviceMetrics={deviceMetrics}
                          mobileSummary={mobileSummary}
                          socialMirrorSummary={socialMirrorSummary}
                          onOpenChannelGuide={() => setShowChannelGuide(true)}
                          onOpenPPCModal={() => setShowPPCModal(true)}
                        />

                        <div className="bg-teal-500/10 rounded-xl p-4 border border-teal-500/20">
                          <p className="text-slate-300 text-center">
                            <span className="text-teal-400 font-semibold">Multiple touchpoints, one unified brand</span>: your message is everywhere your customers are.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

          {/* MARKETING CHANNELS - Extracted Component */}
          <MarketingChannelsSection 
            onOpenPPCModal={() => setShowPPCModal(true)}
            onOpenChannelGuide={() => setShowChannelGuide(true)}
          />

          {/* BRAND CONSISTENCY */}
          <BrandConsistencySection />
          
          
      </ChapterDropdown>

      {/* PPC Search Guide Modal - Extracted Component */}
      <AnimatePresence>
        <PPCSearchGuideModal 
          isOpen={showPPCModal} 
          onClose={() => setShowPPCModal(false)} 
        />
      </AnimatePresence>
      
      {/* Marketing Channel Guide Modal - Extracted Component */}
      <AnimatePresence>
        <MarketingChannelGuideModal 
          isOpen={showChannelGuide} 
          onClose={() => setShowChannelGuide(false)} 
        />
      </AnimatePresence>
    </div>
  );
}
